import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User,
  MapPin,
  Mail,
  Search,
  Building2,
  Users,
  ArrowUpDown,
  ArrowLeft,
  ArrowRight,
  Printer,
  Calendar,
  Coffee,
  Dumbbell,
  ShoppingCart,
  Gift,
  Clock,
  UserCheck,
  Circle,
  Copy,
  Check,
  Phone,
  Smartphone,
  Link2,
  UserPlus,
  Trash2,
  History as HistoryIcon,
  ListFilter,
  Send,
  X as XIcon
} from 'lucide-react';
import { parseDrawioFile, getPeopleData } from '../utils/drawioParser';
import { BsMicrosoftTeams } from 'react-icons/bs';
import LoadingScreen from '../components/LoadingScreen';
import './SeatingChart.css';

// Campus and building hierarchy (static data, defined at module scope).
// Each building carries:
//   - file: the drawio path
//   - drawioName: matches the `building` column in the Pasadena CSV
//   - peopleCsv: CSV path
//   - csvFormat: 'pasadena' | 'hyd' — drives the parser branch in getPeopleData
//   - seatingOffice: storage/API office key for admin-managed seating data
const campuses = [
  {
    id: 'pasadena',
    name: 'Pasadena',
    overviewImage: '/floorplans/campusPasadena.png',
    isBeta: false,
    buildings: [
      { id: 'building1', name: 'Building Main', file: '/floorplans/building1.drawio', baseFontScale: 1, drawioName: 'building1', peopleCsv: '/floorplans/floorplanData.csv', csvFormat: 'pasadena', seatingOffice: 'pasadena' },
      { id: 'building2', name: 'Building 2', file: '/floorplans/building2.drawio', baseFontScale: 1, drawioName: 'building2', peopleCsv: '/floorplans/floorplanData.csv', csvFormat: 'pasadena', seatingOffice: 'pasadena' },
      { id: 'building3', name: 'Building 3', file: '/floorplans/building3.drawio', baseFontScale: 1.2, drawioName: 'building3', peopleCsv: '/floorplans/floorplanData.csv', csvFormat: 'pasadena', seatingOffice: 'pasadena' },
      { id: 'building6', name: 'Building 6', file: '/floorplans/building6.drawio', baseFontScale: 0.85, drawioName: 'building6', peopleCsv: '/floorplans/floorplanData.csv', csvFormat: 'pasadena', seatingOffice: 'pasadena' },
      { id: 'building10', name: 'Gym', file: '/floorplans/building10.drawio', baseFontScale: 1, drawioName: 'building10', peopleCsv: '/floorplans/floorplanData.csv', csvFormat: 'pasadena', seatingOffice: 'pasadena' }
    ],
    clickableZones: [
      {
        buildingId: 'building2',
        name: 'Building 2',
        // Percentage-based coordinates (left, top, width, height)
        left: 46.5,
        top: 43,
        width: 10,
        height: 12,
        rotation: 0
      },
      {
        buildingId: 'building6',
        name: 'Building 6',
        left: 45.25,
        top: 56.5,
        width: 10.5,
        height: 13.5,
        rotation: 0
      },
      {
        buildingId: 'building3',
        name: 'Building 3',
        left: 78.5,
        top: 55.5,
        width: 13,
        height: 13,
        rotation: -15
      },
      {
        buildingId: 'building1',
        name: 'Building Main',
        left: 63.25,
        top: 50.25,
        width: 7,
        height: 25.5,
        rotation: 0
      },
      {
        buildingId: 'building10',
        name: 'Gym',
        left: 45.25,
        top: 71.5,
        width: 10.5,
        height: 13,
        rotation: 0
      }
    ]
  },
  {
    id: 'hyderabad',
    name: 'Hyderabad',
    overviewImage: null,
    isBeta: false,
    buildings: [
      {
        id: 'hyd-floor',
        name: 'HYD Floor',
        file: '/floorplans/HYD-floor-plan.drawio',
        baseFontScale: 1,
        drawioName: 'HYD-floor-plan',
        peopleCsv: '/floorplans/HYD-Pinnacle_Seating_Chart.csv',
        csvFormat: 'hyd',
        seatingOffice: 'hyd'
      }
    ],
    clickableZones: []
  }
];

const formatSeatHistoryDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const normalizeSearchText = (value) =>
  String(value || '').toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');

const normalizeEmployeeEmail = (email) =>
  String(email || '').trim().toLowerCase().replace(/@pinnacletech\.com$/i, '@pinnaclereliability.com');

const seatSearchAliases = (value) => {
  const compact = normalizeSearchText(value);
  if (!compact) return [];

  const aliases = new Set([compact]);
  const wsMatch = compact.match(/^ws0*(\d+)$/);
  if (wsMatch) {
    aliases.add(wsMatch[1]);
  }

  const numberMatch = compact.match(/^0*(\d+)$/);
  if (numberMatch) {
    aliases.add(numberMatch[1]);
    aliases.add(`ws${numberMatch[1]}`);
    aliases.add(`ws${compact}`);
  }

  return [...aliases];
};

const matchesSearchText = (value, query) => {
  const text = String(value || '').toLowerCase();
  const compactValue = normalizeSearchText(value);
  const queryText = String(query || '').toLowerCase().trim();
  const compactQuery = normalizeSearchText(query);
  if (!queryText || !compactQuery) return false;
  return text.includes(queryText) || compactValue.includes(compactQuery);
};

const matchesSeatSearch = (person, query) => {
  const queryAliases = seatSearchAliases(query);
  const seatAliases = [
    ...seatSearchAliases(person.locationId),
    ...seatSearchAliases(person.location),
  ];
  return queryAliases.some(queryAlias =>
    seatAliases.some(seatAlias => seatAlias.includes(queryAlias) || queryAlias.includes(seatAlias))
  );
};

const shouldRunLocalSeatingSearch = (query) => {
  const trimmed = String(query || '').trim();
  return trimmed.length >= 3 || /\d/.test(trimmed);
};

const SeatingChart = () => {
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently, user } = useAuth0();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [buildingData, setBuildingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dynamicScale, setDynamicScale] = useState(1);
  const [userZoom, setUserZoom] = useState(1.2);
  const MIN_ZOOM = 0.25;
  const MAX_ZOOM = 4;
  const ZOOM_STEP = 0.2;
  const [selectedBuilding, setSelectedBuilding] = useState('building2');
  const [selectedCampus, setSelectedCampus] = useState('pasadena');
  const [viewType, setViewType] = useState('campus'); // 'building' or 'campus'
  const [allBuildingsPeopleData, setAllBuildingsPeopleData] = useState({});
  const [toastMessage, setToastMessage] = useState(null);
  const [showEmptyDesks, setShowEmptyDesks] = useState(false);
  const [activeDeskId, setActiveDeskId] = useState(null);
  const [searchPresences, setSearchPresences] = useState({}); // { email: 'Available' | 'Busy' | ... }
  const [floorPresences, setFloorPresences] = useState({}); // { email: 'Available' | 'Busy' | ... }
  const [searchSelectedIndex, setSearchSelectedIndex] = useState(-1);
  const [roomPeopleData, setRoomPeopleData] = useState({}); // { email: { photo, presence } }
  const [copiedField, setCopiedField] = useState(null); // tracks which field was just copied
  const [oofExpanded, setOofExpanded] = useState(false);
  const [graphSearchResults, setGraphSearchResults] = useState([]); // results from Graph user search
  const [showManagePanel, setShowManagePanel] = useState(false);
  const [showSeatRequestPanel, setShowSeatRequestPanel] = useState(false);
  const [manageTab, setManageTab] = useState('add'); // 'add' | 'remove'
  const [manageForm, setManageForm] = useState({ wsNo: '', name: '', email: '' });
  const [seatRequestForm, setSeatRequestForm] = useState({ currentLocation: '', newLocation: '', note: '' });
  const [seatRequestLoading, setSeatRequestLoading] = useState(false);
  const [manageSearch, setManageSearch] = useState('');
  const [manageLoading, setManageLoading] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // { wsNo, name }
  const [confirmDetailsRemove, setConfirmDetailsRemove] = useState(false);
  const [recentlyChangedSeat, setRecentlyChangedSeat] = useState(null); // wsId that just got add/remove'd — pulses briefly
  const [manageGraphResults, setManageGraphResults] = useState([]); // org-wide Graph matches for the Add form
  const [isSeatingAdmin, setIsSeatingAdmin] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [showOptionsPanel, setShowOptionsPanel] = useState(false);
  const [seatingOptionsView, setSeatingOptionsView] = useState('employees');
  const [employeeListSort, setEmployeeListSort] = useState('ws-asc');
  const [seatHistory, setSeatHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [seatRequests, setSeatRequests] = useState([]);
  const [seatRequestsLoading, setSeatRequestsLoading] = useState(false);
  const [seatRequestsError, setSeatRequestsError] = useState('');
  const [seatRequestActionId, setSeatRequestActionId] = useState('');
  const [joinersExitsLog, setJoinersExitsLog] = useState([]);
  const [joinersExitsLoading, setJoinersExitsLoading] = useState(false);
  const [joinersExitsError, setJoinersExitsError] = useState('');
  const [roomAvailability, setRoomAvailability] = useState({}); // { roomId: { currentStatus, events, ... } }
  const [hoveredPerson, setHoveredPerson] = useState(null); // { name, title, email, deskId, presence, x, y }
  const searchInputRef = React.useRef(null);
  const floorPlanWrapperRef = React.useRef(null);
  const dragState = React.useRef({ active: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0, moved: false });

  // Check if the logged-in user is a seating admin
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    getAccessTokenSilently()
      .then(token => fetch('/api/seating/is-admin', { headers: { Authorization: `Bearer ${token}` } }))
      .then(r => r.json())
      .then(data => { if (!cancelled) setIsSeatingAdmin(data.isAdmin === true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthenticated, getAccessTokenSilently]);

  // Drag-to-pan for floor plan
  useEffect(() => {
    const handleMouseMove = (e) => {
      const ds = dragState.current;
      if (!ds.active) return;
      const dx = e.clientX - ds.startX;
      const dy = e.clientY - ds.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) ds.moved = true;
      if (floorPlanWrapperRef.current) {
        floorPlanWrapperRef.current.scrollLeft = ds.scrollLeft - dx;
        floorPlanWrapperRef.current.scrollTop  = ds.scrollTop  - dy;
      }
    };
    const handleMouseUp = () => {
      if (dragState.current.active) {
        dragState.current.active = false;
        if (floorPlanWrapperRef.current) floorPlanWrapperRef.current.style.cursor = 'grab';
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup',   handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup',   handleMouseUp);
    };
  }, []);

  const copyToClipboard = useCallback((text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  }, []);

  // Auth0-backed fetch wrapper. Used for any `/api/*` endpoint that now sits
  // behind jwtCheck + domainGate, including storage-backed seating reads.
  const authFetch = useCallback(async (url, opts = {}) => {
    const token = await getAccessTokenSilently();
    return fetch(url, {
      ...opts,
      headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
    });
  }, [getAccessTokenSilently]);

  // Graph API enriched data
  const emptyGraphData = {
    presence: null, profile: null, calendar: null, photo: null,
    manager: null, directReports: null, workingHours: null, oofMessage: null,
    loading: { presence: false, profile: false, calendar: false, photo: false, manager: false, reports: false, workingHours: false },
    expanded: false, email: null,
  };
  const [graphData, setGraphData] = useState(emptyGraphData);

  // Helper: fetch a single graph endpoint and update state
  const graphFetch = useCallback((key, email) => {
    const encodedEmail = encodeURIComponent(email);
    const stateKey = key === 'workinghours' ? 'workingHours' : key;
    const loadingKey = key === 'reports' ? 'reports' : (key === 'workinghours' ? 'workingHours' : key);
    setGraphData(prev => ({ ...prev, loading: { ...prev.loading, [loadingKey]: true } }));
    fetch(`/api/graph/${key === 'reports' ? 'reports' : key}/${encodedEmail}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        setGraphData(prev => {
          if (prev.email !== email) return prev;
          return { ...prev, [stateKey]: data, loading: { ...prev.loading, [loadingKey]: false } };
        });
      })
      .catch(() => {
        setGraphData(prev => {
          if (prev.email !== email) return prev;
          return { ...prev, [stateKey]: null, loading: { ...prev.loading, [loadingKey]: false } };
        });
      });
  }, []);

  // When person changes: reset data and auto-fetch all profile data
  useEffect(() => {
    const email = selectedPerson?.email;
    setConfirmDetailsRemove(false);
    if (!email) return;

    setGraphData(prev => {
      // Don't reset if same person (avoids flash)
      if (prev.email === email) return prev;
      return { ...emptyGraphData, email };
    });
    setOofExpanded(false);

    // Auto-fetch all profile data
    graphFetch('presence', email);
    graphFetch('photo', email);
    graphFetch('profile', email);
    graphFetch('calendar', email);
    graphFetch('manager', email);
    graphFetch('reports', email);
    graphFetch('workinghours', email);
  }, [selectedPerson?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-fetch OOF message only when calendar shows OOF status
  useEffect(() => {
    const email = graphData.email;
    if (!email || graphData.calendar?.currentStatus !== 'oof') {
      // Clear OOF message if not OOF
      setGraphData(prev => prev.oofMessage ? { ...prev, oofMessage: null } : prev);
      return;
    }
    const controller = new AbortController();
    fetch(`/api/graph/oofmessage/${encodeURIComponent(email)}`, { signal: controller.signal })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        setGraphData(prev => {
          if (prev.email !== email) return prev;
          return { ...prev, oofMessage: data };
        });
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        setGraphData(prev => {
          if (prev.email !== email) return prev;
          return { ...prev, oofMessage: null };
        });
      });
    return () => controller.abort();
  }, [graphData.email, graphData.calendar?.currentStatus]);

  // Fetch presence for search results — triggered only when search term changes
  useEffect(() => {
    const canRunLocalSearch = shouldRunLocalSeatingSearch(searchTerm);
    if (!canRunLocalSearch) {
      setSearchPresences({});
      setSearchSelectedIndex(-1);
      setGraphSearchResults([]);
      return;
    }
    setSearchSelectedIndex(-1);
    if (searchTerm.length < 3) {
      setGraphSearchResults([]);
    }

    // Debounce: wait 500ms after typing stops
    const timer = setTimeout(async () => {
      // Run Graph search and CSV search in parallel
      const csvPeople = allPeopleAcrossBuildings.filter(p => {
        return matchesSearchText(p.name, searchTerm)
          || matchesSearchText(p.email, searchTerm)
          || matchesSearchText(p.buildingName, searchTerm)
          || matchesSearchText(p.location, searchTerm)
          || matchesSearchText(p.locationId, searchTerm)
          || matchesSeatSearch(p, searchTerm);
      });

      let graphUsers = [];
      if (searchTerm.length >= 3) {
        try {
          const res = await fetch(`/api/graph/users/search?q=${encodeURIComponent(searchTerm)}`);
          if (res.ok) graphUsers = await res.json();
        } catch {}
      }

      // Collect all emails for batch presence
      const csvEmails = csvPeople
        .filter(p => !(p.locationId && /^\d+\.CR\./.test(p.locationId)))
        .map(p => p.email?.toLowerCase()).filter(Boolean);
      const graphEmails = graphUsers.map(u => u.email?.toLowerCase()).filter(Boolean);
      const emails = [...new Set([...csvEmails, ...graphEmails])];

      // Fetch presence for all results individually (small set, more reliable than batch)
      let presenceMap = {};
      if (emails.length > 0) {
        const results = await Promise.allSettled(
          emails.map(async (email) => {
            const res = await fetch(`/api/graph/presence/${encodeURIComponent(email)}`);
            if (res.ok) {
              const data = await res.json();
              if (data?.availability) return { email, availability: data.availability };
            }
            return null;
          })
        );
        for (const r of results) {
          if (r.status === 'fulfilled' && r.value) {
            presenceMap[r.value.email.toLowerCase()] = r.value.availability;
          }
        }
      }

      // Update all state together so everything renders in one pass
      setGraphSearchResults(graphUsers);
      setSearchPresences(prev => ({ ...prev, ...presenceMap }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch presence for all people in current building floor plan
  useEffect(() => {
    if (viewType !== 'building' || !buildingData) return;

    const allEmails = [];
    if (buildingData.rooms) {
      buildingData.rooms.forEach(room => {
        (room.people || []).forEach(p => {
          if (p.email) allEmails.push(p.email);
        });
      });
    }
    if (buildingData.cubes) {
      buildingData.cubes.forEach(cube => {
        (cube.people || []).forEach(p => {
          if (p.email) allEmails.push(p.email);
        });
      });
    }

    setFloorPresences({});
    const uniqueEmails = [...new Set(allEmails)];
    const cancelRef = { cancelled: false };

    const fetchAllPresences = async () => {
      if (uniqueEmails.length === 0) return;
      try {
        const BATCH_SIZE = 650;
        const merged = {};
        for (let i = 0; i < uniqueEmails.length; i += BATCH_SIZE) {
          if (cancelRef.cancelled) return;
          const chunk = uniqueEmails.slice(i, i + BATCH_SIZE);
          const res = await fetch('/api/graph/presence/batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ emails: chunk }),
          });
          if (!res.ok || cancelRef.cancelled) return;
          const data = await res.json();
          Object.assign(merged, data);
        }
        if (!cancelRef.cancelled) {
          const presenceMap = {};
          for (const [email, presence] of Object.entries(merged)) {
            if (presence?.availability) {
              presenceMap[email.toLowerCase()] = presence.availability;
            }
          }
          setFloorPresences(presenceMap);
        }
      } catch {}
    };

    const timer = setTimeout(fetchAllPresences, 300);
    return () => { cancelRef.cancelled = true; clearTimeout(timer); };
  }, [viewType, selectedBuilding, buildingData]);

  // Fetch conference room availability when building loads.
  // Prefers the room's own mailbox (room.email) over any people[0].email so
  // HYD's bookable rooms (HYD.Red/Blue/Green/Large/Training) pick up the
  // mailbox attached in drawioParser even when no one is "seated" in them.
  useEffect(() => {
    if (viewType !== 'building' || !buildingData?.conferenceRooms?.length) {
      setRoomAvailability({});
      return;
    }

    const roomEmails = buildingData.conferenceRooms
      .map(r => r.email || r.people?.[0]?.email)
      .filter(Boolean);
    if (roomEmails.length === 0) return;

    const cancelRef = { cancelled: false };

    fetch('/api/graph/calendar/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails: roomEmails }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && !cancelRef.cancelled) {
          const availability = {};
          for (const room of buildingData.conferenceRooms) {
            const email = (room.email || room.people?.[0]?.email)?.toLowerCase();
            if (email && data[email]) {
              availability[room.id] = data[email];
            }
          }
          setRoomAvailability(availability);
        }
      })
      .catch(() => {});

    return () => { cancelRef.cancelled = true; };
  }, [viewType, selectedBuilding, buildingData, selectedCampus]);

  // When a person is selected (via search or click), scroll the floor plan
  // so their seat is centered in view. Runs after the floor plan has loaded
  // and any building switch has settled.
  useEffect(() => {
    const locId = selectedPerson?.locationId || selectedPerson?.location;
    if (!locId || viewType !== 'building' || !buildingData) return;
    // requestAnimationFrame ensures the highlight class is applied before scroll
    const raf = requestAnimationFrame(() => {
      const target = document.querySelector(`[data-location-id="${CSS.escape(locId)}"]`);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedPerson, viewType, buildingData]);

  // Fetch photos and presence for people in selected office room or cabin/HR-style room
  useEffect(() => {
    if (!selectedRoom || !selectedRoom.people?.length) {
      setRoomPeopleData({});
      return;
    }
    const emails = selectedRoom.people.map(p => p.email).filter(Boolean);
    if (emails.length === 0) return;

    setRoomPeopleData({});

    // Fetch photos
    emails.forEach(email => {
      fetch(`/api/graph/photo/${encodeURIComponent(email)}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            setRoomPeopleData(prev => ({
              ...prev,
              [email.toLowerCase()]: { ...prev[email.toLowerCase()], photo: data.photo }
            }));
          }
        })
        .catch(() => {});
    });

    // Fetch presence via batch
    fetch('/api/graph/presence/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setRoomPeopleData(prev => {
            const updated = { ...prev };
            for (const [email, presence] of Object.entries(data)) {
              const key = email.toLowerCase();
              updated[key] = { ...updated[key], presence: presence?.availability };
            }
            return updated;
          });
        }
      })
      .catch(() => {});
  }, [selectedRoom]);

  // Flatten buildings for easy lookup
  const allBuildings = useMemo(() => campuses.flatMap(campus =>
    campus.buildings.map(building => ({ ...building, campusId: campus.id, campusName: campus.name }))
  ), []);
  const selectedBuildingInfo = useMemo(
    () => allBuildings.find(b => b.id === selectedBuilding),
    [allBuildings, selectedBuilding]
  );
  const currentSeatingOffice = selectedBuildingInfo?.seatingOffice || null;
  const zoomEnabled = currentSeatingOffice === 'hyd';
  const canManageCurrentBuilding = viewType === 'building' && isSeatingAdmin && Boolean(currentSeatingOffice);

  // Flatten { [buildingId]: { [locationId]: [{name, email}] } } into a single
  // flat list of people enriched with building/location metadata. Memoized
  // because it was being recomputed in 7+ places per render (search filter,
  // manager lookup, direct-report locations, manage-form lookup, deep-link
  // resolution). Now runs once per change to allBuildingsPeopleData.
  const allPeopleAcrossBuildings = useMemo(() => {
    const list = [];
    Object.keys(allBuildingsPeopleData).forEach(buildingId => {
      const buildingPeople = allBuildingsPeopleData[buildingId];
      const buildingInfo = allBuildings.find(b => b.id === buildingId);
      Object.keys(buildingPeople).forEach(roomId => {
        const peopleInRoom = buildingPeople[roomId];
        peopleInRoom.forEach(person => {
          // For conference rooms (pattern: X.CR.Name), strip the prefix and
          // show just the name for display.
          const conferenceRoomPattern = /^\d+\.CR\.(.+)$/;
          const match = roomId.match(conferenceRoomPattern);
          const displayLocation = match ? match[1] : roomId;
          list.push({
            ...person,
            location: displayLocation,
            locationId: roomId, // Keep original room ID for matching/highlighting
            building: buildingId,
            buildingName: buildingInfo?.name || buildingId,
          });
        });
      });
    });
    return list;
  }, [allBuildingsPeopleData, allBuildings]);

  const assignedPeopleForCurrentCampus = useMemo(() => {
    const getSeatSortNumber = (person) => {
      const value = String(person.locationId || person.location || '');
      const match = value.match(/\bWS\s*0*(\d+)\b/i);
      return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
    };

    const people = allPeopleAcrossBuildings
      .filter(person => {
        const building = allBuildings.find(b => b.id === person.building);
        return building?.campusId === selectedCampus;
      })
      .slice();

    const byNameAsc = (a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    const byEmailAsc = (a, b) =>
      (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' });
    const bySeatAsc = (a, b) => {
      const seatDelta = getSeatSortNumber(a) - getSeatSortNumber(b);
      if (seatDelta !== 0) return seatDelta;
      return (a.locationId || a.location || '').localeCompare(b.locationId || b.location || '', undefined, { numeric: true, sensitivity: 'base' });
    };
    const byBuildingAsc = (a, b) => {
      const buildingDelta = (a.buildingName || '').localeCompare(b.buildingName || '', undefined, { sensitivity: 'base' });
      if (buildingDelta !== 0) return buildingDelta;
      return bySeatAsc(a, b);
    };

    switch (employeeListSort) {
      case 'name-desc':
        return people.sort((a, b) => byNameAsc(b, a) || bySeatAsc(a, b));
      case 'email-asc':
        return people.sort((a, b) => byEmailAsc(a, b) || byNameAsc(a, b));
      case 'ws-desc':
        return people.sort((a, b) => bySeatAsc(b, a) || byNameAsc(a, b));
      case 'building-seat':
        return people.sort((a, b) => byBuildingAsc(a, b) || byNameAsc(a, b));
      case 'name-asc':
        return people.sort((a, b) => byNameAsc(a, b) || bySeatAsc(a, b));
      case 'ws-asc':
      default:
        return people.sort((a, b) => bySeatAsc(a, b) || byNameAsc(a, b));
    }
  }, [allPeopleAcrossBuildings, allBuildings, selectedCampus, employeeListSort]);

  const currentUserSeat = useMemo(() => {
    const userEmail = normalizeEmployeeEmail(user?.email);
    if (!userEmail) return null;
    return allPeopleAcrossBuildings.find(person => {
      if (normalizeEmployeeEmail(person.email) !== userEmail) return false;
      const building = allBuildings.find(item => item.id === person.building);
      return building?.seatingOffice === currentSeatingOffice;
    }) || null;
  }, [allPeopleAcrossBuildings, allBuildings, currentSeatingOffice, user?.email]);

  // Auto-dismiss toast after 3 seconds
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Load people data for every building on mount. Used by global search and
  // deep-link lookup. Shape: { [buildingId]: { [locationId]: [people] } }.
  useEffect(() => {
    const loadAllPeopleData = async () => {
      try {
        const perBuilding = await Promise.all(
          allBuildings.map(async (b) => [b.id, await getPeopleData(b, { authFetch })])
        );
        const allData = Object.fromEntries(perBuilding);
        setAllBuildingsPeopleData(allData);
      } catch (error) {
        console.error('Error loading all people data:', error);
      }
    };
    loadAllPeopleData();
  }, [allBuildings, authFetch]);

  // Deep linking: parse URL params on mount once people data is loaded
  useEffect(() => {
    if (Object.keys(allBuildingsPeopleData).length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const personEmail = params.get('person');
    const buildingParam = params.get('building');

    // Clear URL params after reading (don't pollute browser history)
    if (personEmail || buildingParam) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (personEmail) {
      // Search CSV data for this person
      let foundPerson = null;
      Object.keys(allBuildingsPeopleData).forEach(buildingId => {
        const buildingPeople = allBuildingsPeopleData[buildingId];
        const buildingInfo = allBuildings.find(b => b.id === buildingId);
        Object.keys(buildingPeople).forEach(locationId => {
          buildingPeople[locationId].forEach(p => {
            if (p.email && p.email.toLowerCase() === personEmail.toLowerCase()) {
              foundPerson = {
                name: p.name,
                email: p.email,
                location: locationId,
                locationId: locationId,
                building: buildingId,
                buildingName: buildingInfo?.name || buildingId,
              };
            }
          });
        });
      });

      if (foundPerson) {
        handlePersonClick(foundPerson);
      }
      // Not found — silently ignore
      return; // person param takes priority, skip building-only
    }

    if (buildingParam) {
      const building = allBuildings.find(b => b.id === buildingParam);
      if (building) {
        setSelectedBuilding(buildingParam);
        setSelectedCampus(building.campusId);
        setViewType('building');
      }
    }
  }, [allBuildingsPeopleData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadFloorPlan = async () => {
      setLoading(true);
      setUserZoom(1.2);
      
      try {
        if (viewType === 'campus') {
          // Campus overview - no building data needed
          setBuildingData(null);
          setLoading(false);
        } else {
          // Get the selected building's file
          const building = allBuildings.find(b => b.id === selectedBuilding);
          
          if (!building) {
            setLoading(false);
            return;
          }
          
          // Drawio parse and people-data fetch are independent — fire them in
          // parallel instead of sequentially. The two used to add up; now they
          // overlap and the total wait is whichever finishes last.
          // Also: if the global all-people index already has this building (it
          // gets prefetched at mount), skip the second network round trip and
          // reuse what's already in memory. refreshSeatingData() keeps that index
          // current after admin edits, so reusing it is safe.
          const cachedPeople = allBuildingsPeopleData[building.id];
          const [floorData, peopleData] = await Promise.all([
            parseDrawioFile(building.file),
            cachedPeople ? Promise.resolve(cachedPeople) : getPeopleData(building, { authFetch }),
          ]);
          
          // Merge people data into rooms
          floorData.rooms = floorData.rooms.map(room => ({
            ...room,
            people: peopleData[room.id] || []
          }));
          
          // Merge people data into cubes
          if (floorData.cubes) {
            floorData.cubes = floorData.cubes.map(cube => ({
              ...cube,
              people: peopleData[cube.id] || []
            }));
          }
          
          // Merge people data into conference rooms
          if (floorData.conferenceRooms) {
            floorData.conferenceRooms = floorData.conferenceRooms.map(room => ({
              ...room,
              people: peopleData[room.id] || []
            }));
          }
          
          setBuildingData(floorData);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error loading floor plan:', error);
        setLoading(false);
      }
    };
    
    loadFloorPlan();
    // allBuildingsPeopleData and authFetch are intentionally not deps:
    //   - allBuildingsPeopleData updates on every refresh; adding it would
    //     trigger a full floor reload after every seat edit.
    //   - authFetch's identity is stable enough for our purposes here.
    // The effect captures the latest values at run time via closure.
  }, [selectedBuilding, viewType, allBuildings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate dynamic scale to fit floor plan on screen
  useEffect(() => {
    if (viewType === 'campus' || !buildingData) return;

    const MIN_SCALE = 0.3;

    const calculateScale = () => {
      const floorPlanWidth = buildingData.actualWidth || 1600;
      const floorPlanHeight = buildingData.actualHeight || 900;

      // Detect browser zoom level via visualViewport API
      const browserZoom = window.visualViewport?.scale || 1;
      const wrapperEl = floorPlanWrapperRef.current;

      const isMobile = window.innerWidth <= 768;
      const isSmallMobile = window.innerWidth <= 480;
      // At ≤1200px the CSS grid switches to single-column (info panel moves
      // BELOW the floor plan), so don't subtract its width from the calculation.
      const isWide = window.innerWidth > 1200;

      let availableWidth;
      let availableHeight;
      let scaleFactor;

      if (wrapperEl) {
        const wrapperStyles = window.getComputedStyle(wrapperEl);
        const horizontalPadding =
          (parseFloat(wrapperStyles.paddingLeft) || 0) +
          (parseFloat(wrapperStyles.paddingRight) || 0);
        const verticalPadding =
          (parseFloat(wrapperStyles.paddingTop) || 0) +
          (parseFloat(wrapperStyles.paddingBottom) || 0);

        availableWidth = Math.max(wrapperEl.clientWidth - horizontalPadding - 2, 0) / browserZoom;
        availableHeight = Math.max(wrapperEl.clientHeight - verticalPadding - 2, 0) / browserZoom;
        scaleFactor = zoomEnabled ? 0.99 : 0.985;
      } else {
        let headerHeight;
        let paddingVertical;
        let paddingHorizontal;
        let infoPanelWidth;
        let gap;

        if (isSmallMobile) {
          headerHeight = 60;
          paddingVertical = 20;
          paddingHorizontal = 16;
          infoPanelWidth = 0;
          gap = 0;
          scaleFactor = 0.95;
        } else if (isMobile) {
          headerHeight = 70;
          paddingVertical = 30;
          paddingHorizontal = 24;
          infoPanelWidth = 0;
          gap = 0;
          scaleFactor = 0.92;
        } else {
          headerHeight = 130;
          paddingVertical = 40;
          paddingHorizontal = 48;
          // Only reserve space for the info panel when it actually sits beside the
          // floor plan (viewport > 1200 px). On smaller desktops / laptops the
          // panel stacks below, so the full width is available for the chart.
          infoPanelWidth = isWide ? (window.innerWidth > 1400 ? 320 : 380) : 0;
          gap = isWide ? 24 : 0;
          scaleFactor = 0.95;
        }

        availableWidth = (window.innerWidth - infoPanelWidth - gap - paddingHorizontal) / browserZoom;
        availableHeight = (window.innerHeight - headerHeight - paddingVertical) / browserZoom;
      }

      const scaleX = availableWidth / floorPlanWidth;
      const scaleY = availableHeight / floorPlanHeight;
      const scale = Math.max(
        Math.min(scaleX, scaleY, buildingData.scale) * scaleFactor,
        MIN_SCALE
      );

      setDynamicScale(scale);
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    // Also listen to visualViewport resize for browser zoom changes
    window.visualViewport?.addEventListener('resize', calculateScale);
    return () => {
      window.removeEventListener('resize', calculateScale);
      window.visualViewport?.removeEventListener('resize', calculateScale);
    };
  }, [buildingData, viewType, zoomEnabled]);

  // Reset the Manage Seats form whenever the panel is closed so stale / half-
  // filled data never persists into the next open.
  useEffect(() => {
    if (!showManagePanel) {
      setManageForm({ wsNo: '', name: '', email: '' });
      setManageGraphResults([]);
      setManageTab('add');
      setConfirmRemove(null);
      setManageSearch('');
    }
  }, [showManagePanel]);

  useEffect(() => {
    if (!showSeatRequestPanel) {
      setSeatRequestForm({ currentLocation: '', newLocation: '', note: '' });
      setSeatRequestLoading(false);
    }
  }, [showSeatRequestPanel]);

  // Debounced Graph user lookup for the Manage Seats Add form. Searches the
  // org directory as the admin types a name and surfaces matches in a small
  // dropdown — picking one fills in both Name and Email so they don't have
  // to remember or look up the corporate email by hand.
  useEffect(() => {
    if (!showManagePanel || manageTab !== 'add') {
      setManageGraphResults([]);
      return;
    }
    const q = (manageForm.name || '').trim();
    if (q.length < 3) {
      setManageGraphResults([]);
      return;
    }
    // If a directory suggestion was already selected (email is filled), the
    // form is complete — skip the search so the dropdown doesn't reappear.
    // Typing in the name field always clears email, so this unblocks naturally.
    if (manageForm.email) {
      setManageGraphResults([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/graph/users/search?q=${encodeURIComponent(q)}`, { signal: controller.signal });
        if (res.ok) {
          const data = await res.json();
          setManageGraphResults(Array.isArray(data) ? data.slice(0, 6) : []);
        }
      } catch (err) {
        if (err.name !== 'AbortError') setManageGraphResults([]);
      }
    }, 300);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [manageForm.name, manageForm.email, showManagePanel, manageTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Soft-refresh seating people data without resetting zoom or floor-plan UI state.
  // Refetches the CSV and re-merges it into existing buildingData + the global
  // people index used by search. Called after add/remove so the page reflects
  // changes without a hard browser reload.
  const refreshSeatingData = useCallback(async () => {
    const building = allBuildings.find(b => b.id === selectedBuilding);
    if (!building?.seatingOffice) return;
    try {
      const peopleData = await getPeopleData(building, { authFetch });
      setBuildingData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          rooms: (prev.rooms || []).map(r => ({ ...r, people: peopleData[r.id] || [] })),
          cubes: (prev.cubes || []).map(c => ({ ...c, people: peopleData[c.id] || [] })),
          conferenceRooms: (prev.conferenceRooms || []).map(r => ({ ...r, people: peopleData[r.id] || [] })),
        };
      });
      setAllBuildingsPeopleData(prev => ({ ...prev, [building.id]: peopleData }));
    } catch (err) {
      console.error('refreshSeatingData error:', err);
    }
  }, [selectedBuilding, allBuildings, authFetch]);

  // Background poll: pick up seat changes made by other admins without forcing
  // a manual reload. Pauses when the tab is hidden (no point hammering the API
  // for an invisible page) and refreshes immediately when focus returns so the
  // admin sees up-to-date data the moment they come back.
  useEffect(() => {
    if (!currentSeatingOffice || viewType !== 'building') return;
    let cancelled = false;
    const POLL_MS = 60000;
    const tick = () => { if (!cancelled && !document.hidden) refreshSeatingData(); };
    const intervalId = setInterval(tick, POLL_MS);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [currentSeatingOffice, viewType, refreshSeatingData]);

  // Keyboard polish for the Manage Seats modal:
  //   - Esc cancels an in-flight remove confirmation, or closes the modal.
  //   - Esc is also handled when the panel is open even if a graph dropdown is
  //     showing, so admins can always bail out.
  useEffect(() => {
    if (!showManagePanel) return;
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (confirmRemove) {
        setConfirmRemove(null);
      } else if (manageGraphResults.length > 0) {
        setManageGraphResults([]);
      } else {
        setShowManagePanel(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showManagePanel, confirmRemove, manageGraphResults.length]);

  useEffect(() => {
    if (!showHistoryPanel) return;
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      setShowHistoryPanel(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showHistoryPanel]);

  useEffect(() => {
    const openOptions = (event) => {
      setSeatingOptionsView(event.detail?.view || 'employees');
      setShowOptionsPanel(true);
    };
    window.addEventListener('pinnacle:open-seating-options', openOptions);
    return () => window.removeEventListener('pinnacle:open-seating-options', openOptions);
  }, []);

  useEffect(() => {
    if (!showSeatRequestPanel) return;
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      setShowSeatRequestPanel(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showSeatRequestPanel]);

  useEffect(() => {
    if (!showOptionsPanel) return;
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      setShowOptionsPanel(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showOptionsPanel]);

  useEffect(() => {
    if (!showOptionsPanel || seatingOptionsView !== 'requests' || !isSeatingAdmin || !currentSeatingOffice) return;
    setSeatRequests([]);
    loadSeatRequests();
  }, [showOptionsPanel, seatingOptionsView, isSeatingAdmin, currentSeatingOffice, selectedCampus, selectedBuilding]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showOptionsPanel || seatingOptionsView !== 'joiners' || !isSeatingAdmin || !currentSeatingOffice) return;
    setJoinersExitsLog([]);
    setJoinersExitsLoading(true);
    setJoinersExitsError('');
    (async () => {
      try {
        const officeKey = selectedCampus === 'hyderabad' ? 'hyd' : selectedCampus;
        const res = await authFetch(`/api/graph/seating-tenant-notifications/${officeKey}/log`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load joiners & exits log');
        setJoinersExitsLog(Array.isArray(data.entries) ? data.entries : []);
      } catch (err) {
        setJoinersExitsError(err.message || 'Failed to load joiners & exits log');
      } finally {
        setJoinersExitsLoading(false);
      }
    })();
    window.dispatchEvent(new CustomEvent('pinnacle:joiners-exits-viewed'));
  }, [showOptionsPanel, seatingOptionsView, isSeatingAdmin, currentSeatingOffice, selectedCampus, selectedBuilding]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const campus = campuses.find(c => c.id === selectedCampus);
    const office = {
      id: selectedCampus === 'hyderabad' ? 'hyd' : selectedCampus,
      name: campus?.name || selectedCampus,
    };
    localStorage.setItem('pinnacle-active-seating-office', JSON.stringify(office));
    window.dispatchEvent(new CustomEvent('pinnacle:seating-office-change', { detail: office }));
  }, [selectedCampus]);

  if (authLoading || loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Filter people based on search - merge CSV (local) + Graph (org-wide) results
  const filteredPeople = (() => {
    if (!shouldRunLocalSeatingSearch(searchTerm)) return [];

    const csvResults = allPeopleAcrossBuildings.filter(p => {
      return matchesSearchText(p.name, searchTerm)
        || matchesSearchText(p.email, searchTerm)
        || matchesSearchText(p.buildingName, searchTerm)
        || matchesSearchText(p.location, searchTerm)
        || matchesSearchText(p.locationId, searchTerm)
        || matchesSeatSearch(p, searchTerm);
    });

    // Build lookup maps from Graph results for enriching CSV entries with title
    const graphByEmail = new Map(graphSearchResults.map(u => [u.email?.toLowerCase(), u]));
    const graphByName  = new Map(graphSearchResults.map(u => [u.name?.toLowerCase().trim(), u]));

    // Enrich CSV results with job title from Graph (if available)
    const enrichedCsvResults = csvResults.map(p => {
      const gUser = graphByEmail.get(p.email?.toLowerCase()) || graphByName.get(p.name?.toLowerCase().trim());
      return gUser ? { ...p, title: gUser.jobTitle || gUser.title } : p;
    });

    // Build sets of CSV emails AND names for dedup
    const csvEmails = new Set(csvResults.map(p => p.email?.toLowerCase()).filter(Boolean));
    const csvNames  = new Set(csvResults.map(p => p.name?.toLowerCase().trim()).filter(Boolean));

    // Add Graph results that aren't already in CSV (match by email OR full name)
    const graphOnly = graphSearchResults
      .filter(u =>
        matchesSearchText(u.name, searchTerm)
        || matchesSearchText(u.email, searchTerm)
        || matchesSearchText(u.jobTitle, searchTerm)
        || matchesSearchText(u.department, searchTerm)
        || matchesSearchText(u.officeLocation, searchTerm)
      )
      .filter(u => !csvEmails.has(u.email?.toLowerCase()) && !csvNames.has(u.name?.toLowerCase().trim()))
      .map(u => ({
        name: u.name,
        email: u.email,
        title: u.jobTitle,
        department: u.department,
        officeLocation: u.officeLocation,
        isGraphOnly: true,
      }));

    // Add conference rooms from the current building that match the search.
    // Personal spaces (cabins/HR/huddle) are excluded — their occupants already
    // appear via the people results, so listing the room would just duplicate.
    const buildingInfo = allBuildings.find(b => b.id === selectedBuilding);
    const roomResults = (buildingData?.conferenceRooms || []).filter(room => {
      if (room.isPersonalSpace) return false;
      return matchesSearchText(room.displayName, searchTerm) || matchesSearchText(room.id, searchTerm);
    }).map(room => ({
      name: room.displayName || room.id,
      buildingName: buildingInfo?.name || selectedBuilding,
      location: room.displayName || room.id,
      _isRoom: true,
      _roomData: room,
    }));

    return [...enrichedCsvResults, ...graphOnly, ...roomResults];
  })();

  // ── Seating admin helpers ────────────────────────────────────────────────
  const seatingApi = async (method, body) => {
    if (!currentSeatingOffice) throw new Error('This building is not managed by the seating API');
    const token = await getAccessTokenSilently();
    const res = await fetch(`/api/seating/${currentSeatingOffice}`, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  const openSeatHistory = async () => {
    if (!currentSeatingOffice || !isSeatingAdmin) return;
    setShowHistoryPanel(true);
    setSeatHistory([]);
    setHistoryError('');
    setHistoryLoading(true);
    try {
      const res = await authFetch(`/api/seating/${currentSeatingOffice}/history`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load seating history');
      setSeatHistory(Array.isArray(data.history) ? data.history.slice(0, 10) : []);
    } catch (err) {
      setHistoryError(err.message || 'Failed to load seating history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadSeatRequests = async () => {
    if (!currentSeatingOffice || !isSeatingAdmin) return;
    setSeatRequestsLoading(true);
    setSeatRequestsError('');
    try {
      const res = await authFetch(`/api/seating/${currentSeatingOffice}/requests?status=pending`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load seat change requests');
      setSeatRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch (err) {
      setSeatRequestsError(err.message || 'Failed to load seat change requests');
    } finally {
      setSeatRequestsLoading(false);
    }
  };

  const loadJoinersExitsLog = async () => {
    if (!currentSeatingOffice || !isSeatingAdmin) return;
    setJoinersExitsLoading(true);
    setJoinersExitsError('');
    try {
      const officeKey = currentSeatingOffice === 'pasadena' ? 'pasadena' : 'hyd';
      const res = await authFetch(`/api/graph/seating-tenant-notifications/${officeKey}/log`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load joiners & exits log');
      setJoinersExitsLog(Array.isArray(data.entries) ? data.entries : []);
    } catch (err) {
      setJoinersExitsError(err.message || 'Failed to load joiners & exits log');
    } finally {
      setJoinersExitsLoading(false);
    }
  };

  // Normalize what an admin typed into a drawio-matching seat id. For HYD, bare digits
  // become "WS###" (so "59" → "WS059"); a "WS"-prefixed value just gets stripped
  // of whitespace; non-workstation labels (CABIN-1, HR ROOM) pass through. Pasadena
  // location IDs are already drawio IDs, so they only get trimmed.
  // Note: we don't pad — admins should type the drawio's exact digit count
  // (WS001 vs WS01). Padding here would risk creating ghost rows.
  const normalizeSeatId = (raw) => {
    if (selectedBuildingInfo?.csvFormat !== 'hyd') return (raw || '').trim();
    const trimmed = (raw || '').trim().toUpperCase().replace(/\s+/g, ' ');
    if (/^\d+$/.test(trimmed)) return `WS${trimmed}`;
    if (/^WS\s*\d+$/.test(trimmed)) return trimmed.replace(/\s+/g, '');
    return trimmed;
  };

  // Briefly highlight the affected seat on the floor plan and scroll it into
  // view so the admin sees their change land — clears itself after 3.2s
  // (matches the .recently-changed pulse animation duration).
  const flashSeat = (wsId) => {
    if (!wsId) return;
    setRecentlyChangedSeat(wsId);
    requestAnimationFrame(() => {
      const target = document.querySelector(`[data-location-id="${CSS.escape(wsId)}"]`);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    });
    setTimeout(() => setRecentlyChangedSeat(prev => (prev === wsId ? null : prev)), 3200);
  };

  const openAddSeatPanel = (seatId) => {
    setManageForm({ wsNo: seatId, name: '', email: '' });
    setManageGraphResults([]);
    setConfirmRemove(null);
    setManageSearch('');
    setManageTab('add');
    setShowManagePanel(true);
  };

  const openSeatRequestPanel = () => {
    const currentLocation = currentUserSeat?.locationId || currentUserSeat?.location || '';
    setSeatRequestForm({ currentLocation, newLocation: '', note: '' });
    setShowSeatRequestPanel(true);
  };

  const renderFloorAssignButton = (seatId, label = seatId) => {
    if (!isSeatingAdmin || !currentSeatingOffice) return null;
    return (
      <button
        type="button"
        className="floor-seat-assign-btn"
        title={`Assign someone to ${label}`}
        onClick={(e) => {
          e.stopPropagation();
          openAddSeatPanel(seatId);
        }}
      >
        <UserPlus size={10} />
      </button>
    );
  };

  const handleAddSeat = async () => {
    const { wsNo, name, email } = manageForm;
    if (!wsNo.trim() || !name.trim() || !email.trim()) {
      setToastMessage({ type: 'error', text: 'All fields are required' });
      return;
    }
    setManageLoading(true);
    try {
      const trimmedName = name.trim();
      const normalizedSeat = normalizeSeatId(wsNo);
      const body = {
        wsNo: normalizedSeat,
        location: normalizedSeat,
        name: trimmedName,
        email: email.trim(),
      };
      if (selectedBuildingInfo?.csvFormat === 'pasadena') {
        body.building = selectedBuildingInfo.drawioName || selectedBuildingInfo.id;
      }
      await seatingApi('POST', body);
      setToastMessage({ type: 'success', text: `Added ${trimmedName} to ${normalizedSeat}` });
      setManageForm({ wsNo: '', name: '', email: '' });
      setManageGraphResults([]);
      await refreshSeatingData();
      // Close on success — the toast and the pulse-glow on the seat give the
      // admin all the confirmation they need; leaving the modal open just
      // forces an extra click. (Errors do NOT close — admin needs to fix.)
      setShowManagePanel(false);
      flashSeat(normalizedSeat);
    } catch (err) {
      setToastMessage({ type: 'error', text: err.message });
    } finally {
      setManageLoading(false);
    }
  };

  const handleSubmitSeatRequest = async () => {
    if (!currentSeatingOffice) return;
    const normalizedNewSeat = normalizeSeatId(seatRequestForm.newLocation);
    if (!normalizedNewSeat) {
      setToastMessage({ type: 'error', text: 'New seat is required' });
      return;
    }

    setSeatRequestLoading(true);
    try {
      const body = {
        currentLocation: seatRequestForm.currentLocation.trim(),
        newLocation: normalizedNewSeat,
        note: seatRequestForm.note.trim(),
        name: user?.name || user?.email || '',
      };
      if (selectedBuildingInfo?.csvFormat === 'pasadena') {
        body.building = selectedBuildingInfo.drawioName || selectedBuildingInfo.id;
      }
      const res = await authFetch(`/api/seating/${currentSeatingOffice}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit seat change request');
      setToastMessage({
        type: 'success',
        text: data.duplicate ? 'Seat change request is already pending' : 'Seat change request submitted',
      });
      setShowSeatRequestPanel(false);
    } catch (err) {
      setToastMessage({ type: 'error', text: err.message || 'Failed to submit seat change request' });
    } finally {
      setSeatRequestLoading(false);
    }
  };

  const handleReviewSeatRequest = async (request, action) => {
    if (!currentSeatingOffice || !isSeatingAdmin || !request?.id) return;
    setSeatRequestActionId(`${action}-${request.id}`);
    try {
      const res = await authFetch(`/api/seating/${currentSeatingOffice}/requests/${encodeURIComponent(request.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to review request');
      setToastMessage({
        type: 'success',
        text: action === 'approve'
          ? `Approved ${request.name || request.email} to ${request.newLocation}`
          : `Rejected request for ${request.name || request.email}`,
      });
      await loadSeatRequests();
      if (action === 'approve') {
        await refreshSeatingData();
        flashSeat(request.newLocation);
      }
    } catch (err) {
      setToastMessage({ type: 'error', text: err.message || 'Failed to review request' });
    } finally {
      setSeatRequestActionId('');
    }
  };

  const handleRemoveSeat = async (wsNo, displayName, email) => {
    setManageLoading(true);
    try {
      const normalizedSeat = normalizeSeatId(wsNo);
      const body = {
        wsNo: normalizedSeat,
        location: normalizedSeat,
        email,
      };
      if (selectedBuildingInfo?.csvFormat === 'pasadena') {
        body.building = selectedBuildingInfo.drawioName || selectedBuildingInfo.id;
      }
      await seatingApi('DELETE', body);
      const who = displayName ? `${displayName} (${wsNo})` : wsNo;
      setToastMessage({ type: 'success', text: `Removed ${who}` });
      await refreshSeatingData();
      setManageSearch('');     // reset list filter so re-opening starts clean
      setConfirmRemove(null);
      setShowManagePanel(false);
      flashSeat(normalizedSeat);
      return true;
    } catch (err) {
      setToastMessage({ type: 'error', text: err.message });
      return false;
    } finally {
      setManageLoading(false);
    }
  };
  // ────────────────────────────────────────────────────────────────────────

  const handlePersonClick = (person) => {
    setActiveDeskId(null);

    // If this is actually a conference room entry, handle as room click
    const locationId = person.locationId || person.location;
    const isConfRoom = locationId && /^\d+\.CR\./.test(locationId);

    if (isConfRoom) {
      // Switch to the correct building if needed
      if (person.building && person.building !== selectedBuilding) {
        setSelectedBuilding(person.building);
        const building = allBuildings.find(b => b.id === person.building);
        if (building) {
          setSelectedCampus(building.campusId);
          setViewType('building');
          setToastMessage(`Switched to ${building.name}`);
        }
      } else if (viewType === 'campus' && person.building) {
        const building = allBuildings.find(b => b.id === person.building);
        if (building) {
          setSelectedCampus(building.campusId);
          setViewType('building');
        }
      }

      // Create a conference room object with displayName for the room card
      const displayName = locationId.split('.CR.')[1] || person.name;
      setSelectedRoom({
        id: locationId,
        displayName: displayName,
        people: [{ name: person.name, email: person.email }],
      });
      setSelectedPerson(null);
      setSearchTerm('');
      return;
    }

    // Graph-only people don't have a seat — just show their profile
    if (person.isGraphOnly) {
      setSelectedPerson({
        name: person.name,
        email: person.email,
        title: person.title,
        officeLocation: person.officeLocation,
        isGraphOnly: true,
      });
      setSelectedRoom(null);
      setSearchTerm('');
      return;
    }

    // If person is in a different building, switch to that building
    if (person.building && person.building !== selectedBuilding) {
      const building = allBuildings.find(b => b.id === person.building);
      setSelectedBuilding(person.building);
      if (building) {
        setSelectedCampus(building.campusId);
        setViewType('building');
        setToastMessage(`Switched to ${building.name}`);
      }
    } else if (viewType === 'campus' && person.building) {
      // If we're on campus view but person is in the currently selected building,
      // still need to switch to building view to show the floor plan
      const building = allBuildings.find(b => b.id === person.building);
      if (building) {
        setSelectedCampus(building.campusId);
        setViewType('building');
      }
    }
    
    const selectedPersonData = {
      name: person.name,
      location: person.location,
      locationId: person.locationId || person.location, // Use locationId for matching, fallback to location
      email: person.email,
      phone: person.phone,
      title: person.title,
      building: person.building,
      buildingName: person.buildingName
    };
    
    setSelectedPerson(selectedPersonData);
    setSelectedRoom(null); // Clear room selection when selecting a person
    setSearchTerm(''); // Clear search after selection
  };

  const handleRoomClick = (room) => {
    setActiveDeskId(null);
    setSelectedRoom(room);
    setSelectedPerson(null);
    setSearchTerm('');
  };

  // Switch campuses. If the target campus has a campus overview image we land
  // on the overview; otherwise (e.g. HYD) we jump straight into its first
  // building floor plan.
  const handleCampusClick = (campusId) => {
    const campus = campuses.find(c => c.id === campusId);
    if (!campus) return;
    setSelectedCampus(campusId);
    setSearchTerm('');
    setSelectedPerson(null);
    setSelectedRoom(null);
    if (campus.overviewImage) {
      setViewType('campus');
    } else {
      const firstBuilding = campus.buildings[0];
      if (firstBuilding) {
        setSelectedBuilding(firstBuilding.id);
        setViewType('building');
      }
    }
  };

  const currentCampus = campuses.find(c => c.id === selectedCampus);

  const handleZoneClick = (buildingId) => {
    const building = allBuildings.find(b => b.id === buildingId);
    if (building) {
      setSelectedBuilding(buildingId);
      setSelectedCampus(building.campusId);
      setViewType('building');
      setSearchTerm('');
      setSelectedPerson(null);
      setSelectedRoom(null);
    }
  };

  const highlightLocation = (locationId) => {
    // Highlight if location matches search results
    if (filteredPeople.some(p => (p.locationId || p.location) === locationId)) {
      return true;
    }
    // Highlight if location matches selected person (use locationId for accurate matching)
    if (selectedPerson && (selectedPerson.locationId || selectedPerson.location) === locationId) {
      return true;
    }
    // Highlight if location matches selected room
    if (selectedRoom && selectedRoom.id === locationId) {
      return true;
    }
    return false;
  };

  const effectiveScale = dynamicScale * (zoomEnabled ? userZoom : 1);

  return (
    <div className="seating-chart-visual">
      {/* Header */}
      <motion.div
        className="chart-header"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="header-left">
          <Building2 size={32} className="header-icon" />
          <span
            className="chart-page-title"
            onClick={() => {
              // Only return to the campus overview if this campus has one.
              if (currentCampus?.overviewImage) {
                setViewType('campus');
                setSearchTerm('');
                setSelectedPerson(null);
                setSelectedRoom(null);
              }
            }}
            style={{ cursor: currentCampus?.overviewImage ? 'pointer' : 'default' }}
          >
            {currentCampus?.name}
          </span>

          <div className="campus-tabs">
            {campuses.map(campus => (
              <button
                key={campus.id}
                className={`campus-tab ${campus.id === selectedCampus ? 'active' : ''}`}
                onClick={() => handleCampusClick(campus.id)}
              >
                {campus.name}
                {campus.isBeta && <span className="campus-beta-pill">BETA</span>}
              </button>
            ))}
          </div>

          <div className="building-tabs">
            {currentCampus?.buildings.map(building => (
              <button
                key={building.id}
                className={`building-tab ${viewType === 'building' && building.id === selectedBuilding ? 'active' : ''}`}
                onClick={() => {
                  setSelectedBuilding(building.id);
                  setViewType('building');
                  setSearchTerm('');
                  setSelectedPerson(null);
                  setSelectedRoom(null);
                }}
              >
                {building.name}
              </button>
            ))}
          </div>

          {viewType === 'building' && buildingData?.cubes?.some(c => !c.people || c.people.length === 0) && (
            <button
              className={`empty-desk-toggle ${showEmptyDesks ? 'active' : ''}`}
              onClick={() => {
                setShowEmptyDesks(prev => {
                  // When turning ON (showing empty desks), clear any open desk/
                  // cabin detail so the right panel shows the empty seats list.
                  if (!prev) {
                    setSelectedRoom(null);
                    setSelectedPerson(null);
                  }
                  return !prev;
                });
              }}
            >
              <MapPin size={14} />
              <span>{showEmptyDesks ? 'Hide Empty Desks' : 'Show Empty Desks'}</span>
            </button>
          )}
          {currentSeatingOffice && (
            <button
              className="seat-change-request-btn"
              onClick={openSeatRequestPanel}
            >
              <Send size={14} />
              <span>Request Seat Change</span>
            </button>
          )}
          {canManageCurrentBuilding && (
            <button
              className="manage-seats-btn"
              onClick={() => setShowManagePanel(true)}
            >
              <UserPlus size={14} />
              <span>Manage Seats</span>
            </button>
          )}
          {canManageCurrentBuilding && (
            <button
              className="seat-history-btn"
              onClick={openSeatHistory}
            >
              <HistoryIcon size={14} />
              <span>History</span>
            </button>
          )}
        </div>

        {/* Back Button */}
        <button
          className="back-to-home-btn"
          onClick={() => window.location.href = '/'}
        >
          <ArrowLeft size={20} />
          <span>Back to Platform</span>
        </button>
      </motion.div>

      {/* Main Content Area */}
      <div className="chart-main-layout">

        {/* Floor Plan or Campus Overview */}
        <div className="floor-plan-section">
          {/* Toast notification — used for building switches and seat updates.
              Accepts either a string ("Switched to Building 2") or an object
              { text, type: 'success'|'error'|'info' } for richer feedback. */}
          {toastMessage && (
            <motion.div
              className={`building-toast building-toast-${typeof toastMessage === 'object' ? toastMessage.type || 'info' : 'info'}`}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {typeof toastMessage === 'object' && toastMessage.type === 'success' ? (
                <Check size={14} />
              ) : typeof toastMessage === 'object' && toastMessage.type === 'error' ? (
                <XIcon size={14} />
              ) : (
                <MapPin size={14} />
              )}
              <span>{typeof toastMessage === 'object' ? toastMessage.text : toastMessage}</span>
            </motion.div>
          )}

          {/* Beta banner — shown when the selected campus is flagged isBeta */}
          {viewType === 'building' && currentCampus?.isBeta && (
            <div className="beta-banner" role="note">
              <span className="beta-banner-pill">BETA</span>
              <span className="beta-banner-text">
                {currentCampus.name} floor plan is a work in progress. Some seats and rooms may be missing or unverified.
              </span>
            </div>
          )}

          {viewType === 'campus' ? (
            <div className="campus-overview-wrapper">
              <motion.div
                className="campus-overview"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="campus-image-container">
                  <img 
                    src={campuses.find(c => c.id === selectedCampus)?.overviewImage} 
                    alt={`${campuses.find(c => c.id === selectedCampus)?.name} Overview`}
                    className="campus-overview-image"
                  />
                  {/* Clickable zones overlay */}
                  {campuses.find(c => c.id === selectedCampus)?.clickableZones?.map((zone, idx) => (
                    <div
                      key={idx}
                      className="campus-zone"
                      style={{
                        left: `${zone.left}%`,
                        top: `${zone.top}%`,
                        width: `${zone.width}%`,
                        height: `${zone.height}%`,
                        transform: `rotate(${zone.rotation || 0}deg)`
                      }}
                      onClick={() => handleZoneClick(zone.buildingId)}
                    >
                      <span className="campus-zone-label">{zone.name}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          ) : buildingData ? (
          <div
            ref={floorPlanWrapperRef}
            className={`floor-plan-wrapper${zoomEnabled ? '' : ' floor-plan-wrapper--fit'}`}
            onMouseDown={(e) => {
              // Don't hijack clicks on interactive elements
              if (e.target.closest('.cube, .room, .conference-room, .person-tag, .facility-coffee, button, a')) return;
              dragState.current = { active: true, startX: e.clientX, startY: e.clientY, scrollLeft: floorPlanWrapperRef.current.scrollLeft, scrollTop: floorPlanWrapperRef.current.scrollTop, moved: false };
              floorPlanWrapperRef.current.style.cursor = 'grabbing';
              e.preventDefault();
            }}
            onWheel={(e) => {
              if (zoomEnabled && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
                setUserZoom(prev => +Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev + delta)).toFixed(2));
              }
            }}
          >
            <div style={{
              width: `${(buildingData.actualWidth || 1600) * effectiveScale}px`,
              height: `${(buildingData.actualHeight || 900) * effectiveScale}px`,
              margin: 'auto'
            }}>
            <motion.div
              className={`floor-plan${effectiveScale < 0.45 ? ' floor-plan-compact' : ''}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              style={{
                position: 'relative',
                width: `${buildingData.actualWidth || 1600}px`,
                height: `${buildingData.actualHeight || 900}px`,
                transform: `scale(${effectiveScale})`,
                transformOrigin: 'top left',
                '--font-scale': allBuildings.find(b => b.id === selectedBuilding)?.baseFontScale || 1
              }}
              onClick={(e) => {
                if (dragState.current.moved) { dragState.current.moved = false; return; }
                setActiveDeskId(null);
                setSelectedPerson(null);
                setSelectedRoom(null);
              }}
            >
            {/* Walls */}
            {buildingData.walls && buildingData.walls.map((wall, idx) => (
              <div
                key={`wall-${idx}`}
                className={`wall ${wall.type}`}
                style={{
                  position: 'absolute',
                  left: `${wall.left}px`,
                  top: `${wall.top}px`,
                  width: `${wall.width}px`,
                  height: `${wall.height}px`
                }}
              />
            ))}

            {/* Doors */}
            {buildingData.doors && buildingData.doors.map((door, idx) => (
              <div
                key={`door-${idx}`}
                className="wall door"
                style={{
                  position: 'absolute',
                  left: `${door.left}px`,
                  top: `${door.top}px`,
                  width: `${door.width}px`,
                  height: `${door.height}px`,
                }}
              />
            ))}

            {/* Rooms */}
            {buildingData.rooms && buildingData.rooms.map((room) => {
              const showQuickAssign = isSeatingAdmin && !!currentSeatingOffice;

              return (
                <div
                  key={room.id}
                  data-location-id={room.id}
                  className={`room ${room.type} ${showQuickAssign ? 'assignable-room' : ''} ${highlightLocation(room.id) ? 'highlighted' : ''} ${recentlyChangedSeat === room.id ? 'recently-changed' : ''}`}
                  style={{
                    position: 'absolute',
                    left: `${room.left}px`,
                    top: `${room.top}px`,
                    width: `${room.width}px`,
                    height: `${room.height}px`
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRoomClick(room);
                  }}
                >
                  {showQuickAssign && renderFloorAssignButton(room.id)}
                  <div className="room-label">{room.id}</div>
                  {room.people && room.people.length > 0 && !/^\d+\.CR\./.test(room.id) && (
                    <div className="room-people">
                      {room.people.slice(0, 4).map((person, idx) => (
                        <div
                          key={idx}
                          className={`person-tag ${person.email && floorPresences[person.email.toLowerCase()] ? `presence-pill-${floorPresences[person.email.toLowerCase()].toLowerCase()}` : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePersonClick({
                              ...person,
                              location: room.id,
                              building: selectedBuilding,
                              buildingName: allBuildings.find(b => b.id === selectedBuilding)?.name
                            });
                          }}
                          onMouseEnter={(e) => {
                            setHoveredPerson({
                              name: person.name,
                              title: person.title,
                              email: person.email,
                              deskId: room.id,
                              presence: person.email ? floorPresences[person.email.toLowerCase()] : null,
                              x: e.clientX,
                              y: e.clientY,
                            });
                          }}
                          onMouseLeave={() => setHoveredPerson(null)}
                        >
                          <span>{person.name.split(' ')[0]}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Cubes/Desks */}
            {buildingData.cubes && buildingData.cubes.map((cube) => {
              const hasPerson = cube.people && cube.people.length > 0;
              const person = hasPerson ? cube.people[0] : null;
              const showQuickAssign = !hasPerson && isSeatingAdmin;
              
              return (
                <div
                  key={cube.id}
                  data-location-id={cube.id}
                  className={`cube ${!hasPerson ? 'empty' : ''} ${showQuickAssign ? 'assignable-empty' : ''} ${highlightLocation(cube.id) ? 'highlighted' : ''} ${!hasPerson && showEmptyDesks ? 'empty-highlighted' : ''} ${activeDeskId === cube.id ? 'active-desk' : ''} ${recentlyChangedSeat === cube.id ? 'recently-changed' : ''}`}
                  style={{
                    position: 'absolute',
                    left: `${cube.left}px`,
                    top: `${cube.top}px`,
                    width: `${cube.width}px`,
                    height: `${cube.height}px`
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasPerson) {
                      handlePersonClick({
                        ...person,
                        location: cube.id,
                        building: selectedBuilding,
                        buildingName: allBuildings.find(b => b.id === selectedBuilding)?.name
                      });
                      return;
                    }
                    if (showQuickAssign) {
                      setActiveDeskId(prev => prev === cube.id ? null : cube.id);
                      setSelectedPerson(null);
                      setSelectedRoom(null);
                      setSearchTerm('');
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (hasPerson) {
                      setHoveredPerson({
                        name: person.name,
                        title: person.title,
                        email: person.email,
                        deskId: cube.displayLabel || cube.id,
                        presence: person.email ? floorPresences[person.email.toLowerCase()] : null,
                        x: e.clientX,
                        y: e.clientY,
                      });
                    }
                  }}
                  onMouseLeave={() => setHoveredPerson(null)}
                >
                  {showQuickAssign && (
                    <button
                      type="button"
                      className="floor-seat-assign-btn"
                      title={`Assign someone to ${cube.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDeskId(cube.id);
                        openAddSeatPanel(cube.id);
                      }}
                    >
                      <UserPlus size={10} />
                    </button>
                  )}
                  <div className="cube-label">{cube.displayLabel || cube.id}</div>
                  {hasPerson && (
                    <div className="cube-people">
                      <div className={`cube-person-name ${person.email && floorPresences[person.email.toLowerCase()] ? `presence-pill-${floorPresences[person.email.toLowerCase()].toLowerCase()}` : ''}`}>{person.name.split(' ')[0]}</div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Conference Rooms */}
            {buildingData.conferenceRooms && buildingData.conferenceRooms.map((room) => {
              const showQuickAssign = isSeatingAdmin && !!currentSeatingOffice && room.isPersonalSpace;

              return (
                <div
                  key={room.id}
                  data-location-id={room.id}
                  className={`conference-room ${showQuickAssign ? 'assignable-room' : ''} ${highlightLocation(room.id) ? 'highlighted' : ''} ${roomAvailability[room.id] ? (roomAvailability[room.id].currentStatus === 'free' ? 'room-available' : 'room-occupied') : ''} ${room.roomColor ? `room-color-${room.roomColor}` : ''} ${recentlyChangedSeat === room.id ? 'recently-changed' : ''}`}
                  style={{
                    position: 'absolute',
                    left: `${room.left}px`,
                    top: `${room.top}px`,
                    width: `${room.width}px`,
                    height: `${room.height}px`
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRoomClick(room);
                  }}
                >
                  {showQuickAssign && renderFloorAssignButton(room.id, room.displayName || room.id)}
                  <div className="conference-room-label">{room.displayName}</div>
                  {room.people && room.people.length > 0 && (
                    <div className="conf-room-people">
                      {room.people.map((person, idx) => (
                        <div
                          key={idx}
                          className={`conf-room-person-name ${person.email && floorPresences[person.email.toLowerCase()] ? `presence-pill-${floorPresences[person.email.toLowerCase()].toLowerCase()}` : ''}`}
                          onMouseEnter={(e) => {
                            // Hover tooltip parity with regular desks / office
                            // rooms — cabins and HR Room occupants get the same
                            // name/title/presence card on hover.
                            e.stopPropagation();
                            setHoveredPerson({
                              name: person.name,
                              title: person.title,
                              email: person.email,
                              deskId: room.displayName || room.id,
                              presence: person.email ? floorPresences[person.email.toLowerCase()] : null,
                              x: e.clientX,
                              y: e.clientY,
                            });
                          }}
                          onMouseLeave={() => setHoveredPerson(null)}
                        >
                          {person.name.split(' ')[0]}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Facilities */}
            {buildingData.facilities && buildingData.facilities.filter(f => f.type !== 'entrance').map((facility, idx) => {
              // Clean label: replace <div> with line breaks, then remove other HTML tags
              const cleanLabel = (facility.label || facility.id)
                .replace(/<div>/gi, '\n')
                .replace(/<\/div>/gi, '')
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<[^>]*>/g, '')
                .trim();
              
              return (
                <div
                  key={`facility-${idx}`}
                  className={`facility facility-${facility.type === 'market' || facility.type === 'peakstore' ? 'printer' : facility.type}`}
                  style={{
                    position: 'absolute',
                    left: `${facility.left}px`,
                    top: `${facility.top}px`,
                    width: `${facility.width}px`,
                    height: `${facility.height}px`,
                    cursor: facility.type === 'coffee' ? 'pointer' : 'default'
                  }}
                  onClick={(e) => {
                    if (facility.type === 'coffee') {
                      e.stopPropagation();
                      window.open('https://peakcoffee.square.site/', '_blank');
                    }
                  }}
                >
                  {facility.type === 'printer' ? (
                    <Printer size={24} className="facility-icon" />
                  ) : facility.type === 'coffee' ? (
                    <Coffee size={24} className="facility-icon" />
                  ) : facility.type === 'gym' ? (
                    <Dumbbell size={24} className="facility-icon" />
                  ) : facility.type === 'market' ? (
                    <ShoppingCart size={24} className="facility-icon" />
                  ) : facility.type === 'peakstore' ? (
                    <Gift size={24} className="facility-icon" />
                  ) : facility.type === 'repro' ? (
                    <>
                      <Printer size={20} className="facility-icon" />
                      <span className="repro-label">Repro</span>
                    </>
                  ) : facility.type === 'cafeteria' ? (
                    <div className="facility-label cafeteria-label">Cafeteria</div>
                  ) : facility.type === 'locker' ? (
                    null /* lockers render as plain boxes — labels are noise */
                  ) : (
                    <div className="facility-label">{cleanLabel}</div>
                  )}
                </div>
              );
            })}
          </motion.div>
          </div>
          </div>
          ) : (
            <div className="floor-plan-wrapper">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '400px',
                fontSize: '1.2rem',
                color: '#666'
              }}>
                Loading floor plan...
              </div>
            </div>
          )}

          {/* Zoom Controls */}
          {viewType === 'building' && buildingData && zoomEnabled && (
            <div className="zoom-controls">
              <button
                className="zoom-btn"
                onClick={() => setUserZoom(prev => +Math.max(MIN_ZOOM, prev - ZOOM_STEP).toFixed(2))}
                title="Zoom out"
                disabled={userZoom <= MIN_ZOOM}
              >−</button>
              <button
                className="zoom-level"
                onClick={() => setUserZoom(1)}
                title="Reset zoom"
              >
                {Math.round(userZoom * 100)}%
              </button>
              <button
                className="zoom-btn"
                onClick={() => setUserZoom(prev => +Math.min(MAX_ZOOM, prev + ZOOM_STEP).toFixed(2))}
                title="Zoom in"
                disabled={userZoom >= MAX_ZOOM}
              >+</button>
            </div>
          )}
        </div>

        {/* Right-hand Info Panel */}
        <div className="info-panel">
          {/* Search Bar in Panel */}
          <div className="panel-search-container">
            <Search size={18} className="panel-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={currentSeatingOffice === 'hyd' ? 'Search by name or WS ID...' : 'Search by name or location...'}
              value={searchTerm}
              onChange={(e) => {
                const sanitized = e.target.value.replace(/[<>"'`;\\]/g, '').slice(0, 100);
                setSearchTerm(sanitized);
                setSearchPresences({});
                if (e.target.value) {
                  setSelectedPerson(null);
                  setSelectedRoom(null);
                }
              }}
              onKeyDown={(e) => {
                if (!searchTerm || filteredPeople.length === 0) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSearchSelectedIndex(prev => Math.min(prev + 1, filteredPeople.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSearchSelectedIndex(prev => Math.max(prev - 1, 0));
                } else if (e.key === 'Enter' && searchSelectedIndex >= 0) {
                  e.preventDefault();
                  const item = filteredPeople[searchSelectedIndex];
                  if (item._isRoom) handleRoomClick(item._roomData);
                  else handlePersonClick(item);
                } else if (e.key === 'Escape') {
                  setSearchTerm('');
                  searchInputRef.current?.blur();
                }
              }}
              className="panel-search-input"
            />
            {searchTerm && (
              <button 
                className="panel-search-clear"
                onClick={() => setSearchTerm('')}
              >
                ×
              </button>
            )}
          </div>

          {searchTerm && filteredPeople.length > 0 ? (
            <motion.div
              className="search-results-panel"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="details-header">
                <h3>Search Results</h3>
                <button className="close-details-btn" onClick={() => setSearchTerm('')}>×</button>
              </div>
              
              <div className="results-header-panel">
                <Users size={18} />
                <span>{filteredPeople.length} {filteredPeople.length === 1 ? 'Result' : 'Results'} Found</span>
              </div>
              
              <div className="results-list-panel">
                {filteredPeople.map((person, idx) => {
                  const presenceStatus = searchPresences[person.email?.toLowerCase()];
                  const isConfRoom = (person.locationId && /^\d+\.CR\./.test(person.locationId)) || person._isRoom;
                  return (
                  <div
                    key={idx}
                    className={`result-item-panel ${idx === searchSelectedIndex ? 'keyboard-selected' : ''} ${isConfRoom ? 'conf-room-result' : ''}`}
                    onClick={() => person._isRoom ? handleRoomClick(person._roomData) : handlePersonClick(person)}
                  >
                    <div className="result-item-header">
                      {isConfRoom ? (
                        <Calendar size={16} className="result-room-icon" />
                      ) : presenceStatus ? (
                        <Circle size={10} fill="currentColor" className={`result-presence-dot presence-dot-${presenceStatus.toLowerCase()}`} />
                      ) : (
                        <User size={18} className="result-user-icon" />
                      )}
                      <div className="result-item-content">
                        <div className="result-name">{person.name}{isConfRoom ? ' (Room)' : ''}</div>
                        {person.title && !isConfRoom && <div className="result-title">{person.title}</div>}
                        {isConfRoom && person._roomData?.capacity && (
                          <div className="result-title"><Users size={11} style={{display:'inline',marginRight:3}} />Capacity: {person._roomData.capacity}</div>
                        )}
                      </div>
                    </div>
                    <div className="result-item-location">
                      {person.isGraphOnly ? (
                        <>
                          <MapPin size={14} />
                          <span>{person.officeLocation || 'No location'}</span>
                        </>
                      ) : (
                        <>
                          <Building2 size={14} />
                          <span>{person.buildingName}</span>
                          <span className="location-separator">•</span>
                          <MapPin size={14} />
                          <span>{person.location}</span>
                        </>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </motion.div>
          ) : selectedPerson ? (
            <motion.div
              className="person-details"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Compact header: avatar + name + presence */}
              <div className="person-card-header">
                <div className="details-avatar">
                  {graphData.photo?.photo ? (
                    <img src={graphData.photo.photo} alt={selectedPerson.name} className="avatar-photo" />
                  ) : (
                    <span className="avatar-initials">
                      {selectedPerson.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="person-card-name-block">
                  <h2 className="details-name">{selectedPerson.name}</h2>
                  {graphData.profile ? (
                    <>
                      {graphData.profile.jobTitle && <p className="details-title">{graphData.profile.jobTitle}</p>}
                      {graphData.profile.department && <p className="details-department">{graphData.profile.department}</p>}
                    </>
                  ) : (
                    selectedPerson.title && <p className="details-title">{selectedPerson.title}</p>
                  )}
                  {graphData.presence && (
                    <div className={`presence-badge presence-${graphData.presence.availability?.toLowerCase()}`}>
                      <Circle size={8} fill="currentColor" />
                      <span>{{
                        Available: 'Available',
                        AvailableIdle: 'Available - Idle',
                        Busy: 'Busy',
                        BusyIdle: 'Busy - Idle',
                        DoNotDisturb: 'Do Not Disturb',
                        Away: 'Away',
                        BeRightBack: 'Be Right Back',
                        Offline: 'Offline',
                        PresenceUnknown: 'Presence Unknown',
                      }[graphData.presence.availability] || graphData.presence.availability || 'Unknown'}</span>
                    </div>
                  )}
                </div>
                <button
                  className="close-details-btn"
                  onClick={() => {
                    const url = `${window.location.origin}${window.location.pathname}?person=${encodeURIComponent(selectedPerson.email)}`;
                    copyToClipboard(url, 'link');
                  }}
                  title="Copy link to person"
                  style={{ marginRight: 4 }}
                >
                  {copiedField === 'link' ? <Check size={14} /> : <Link2 size={14} />}
                </button>
                <button className="close-details-btn" onClick={() => setSelectedPerson(null)}>×</button>
              </div>

              <div className="details-info">
                {selectedPerson.isGraphOnly ? (
                  <div className="info-row">
                    <MapPin size={18} />
                    <span>{selectedPerson.officeLocation || 'No location'}</span>
                  </div>
                ) : (
                  <>
                    <div className="info-row">
                      <Building2 size={18} />
                      <span>{selectedPerson.buildingName}</span>
                    </div>
                    <div className="info-row">
                      <MapPin size={18} />
                      <span>{selectedPerson.location}</span>
                      {/* Admin shortcut: relocate this person without retyping
                          their name/email. Only shown for storage-managed seats. */}
                      {isSeatingAdmin && allBuildings.find(b => b.id === selectedPerson.building)?.seatingOffice && !selectedPerson.isGraphOnly && (
                        <>
                          <button
                            className="info-row-action move-person-action"
                            title="Reassign this person to a different seat"
                            onClick={() => {
                              setManageForm({ wsNo: '', name: selectedPerson.name || '', email: selectedPerson.email || '' });
                              setManageGraphResults([]);
                              setManageTab('add');
                              setShowManagePanel(true);
                            }}
                          >
                            Move…
                          </button>
                          {confirmDetailsRemove ? (
                            <div className="details-remove-confirm">
                              <span>Remove?</span>
                              <button
                                className="details-confirm-yes"
                                disabled={manageLoading}
                                onClick={async () => {
                                  const removed = await handleRemoveSeat(
                                    selectedPerson.locationId || selectedPerson.location,
                                    selectedPerson.name,
                                    selectedPerson.email
                                  );
                                  if (removed) {
                                    setConfirmDetailsRemove(false);
                                    setSelectedPerson(null);
                                  }
                                }}
                              >
                                Yes
                              </button>
                              <button
                                className="details-confirm-no"
                                disabled={manageLoading}
                                onClick={() => setConfirmDetailsRemove(false)}
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              className="info-row-action remove-person-action"
                              title="Remove this seating assignment"
                              disabled={manageLoading}
                              onClick={() => setConfirmDetailsRemove(true)}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
                <div className="info-row">
                  <span>{selectedPerson.email}</span>
                  <button className="info-row-action" onClick={() => copyToClipboard(selectedPerson.email, 'email')} title="Copy email">
                    {copiedField === 'email' ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                  <a href={`mailto:${selectedPerson.email}`} className="info-row-action" title="Send email">
                    <Mail size={14} />
                  </a>
                  <button
                    className="info-row-action"
                    onClick={() => window.open(`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(selectedPerson.email)}`, '_blank')}
                    title="Open Teams Chat"
                  >
                    <BsMicrosoftTeams size={14} />
                  </button>
                </div>

                {/* Phone numbers from Graph */}
                {graphData.profile && (
                  <>
                    {graphData.profile.businessPhones?.length > 0 && (
                      <motion.div className="info-row" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <Phone size={16} />
                        <span>{graphData.profile.businessPhones[0]}</span>
                        <span className="info-row-label">Work</span>
                        <button className="info-row-action" onClick={() => copyToClipboard(graphData.profile.businessPhones[0], 'phone')} title="Copy phone">
                          {copiedField === 'phone' ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      </motion.div>
                    )}
                    {graphData.profile.mobilePhone && (
                      <motion.div className="info-row" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <Smartphone size={16} />
                        <span>{graphData.profile.mobilePhone}</span>
                        <span className="info-row-label">Mobile</span>
                        <button className="info-row-action" onClick={() => copyToClipboard(graphData.profile.mobilePhone, 'mobile')} title="Copy mobile">
                          {copiedField === 'mobile' ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      </motion.div>
                    )}
                  </>
                )}

                {/* Loading shimmer for profile */}
                {graphData.loading.profile && (
                  <div className="info-row shimmer-row"><div className="shimmer-bar" /></div>
                )}
              </div>

              {/* Profile sections */}
              <div className="expanded-profile">
                    {/* Schedule Section */}
                    <div className="profile-section">
                      <div className="section-label">
                        <Calendar size={14} />
                        <span>Schedule</span>
                      </div>
                      {graphData.presence?.statusMessage && graphData.calendar?.currentStatus !== 'oof' && (
                        <div className="status-message-container">
                          <div className="status-message-text">
                            {graphData.presence.statusMessage}
                          </div>
                        </div>
                      )}
                      {graphData.loading.calendar ? (
                        <div className="shimmer-block" />
                      ) : graphData.calendar ? (
                        <div className="calendar-section">
                          <div className={`calendar-status ${graphData.calendar.currentStatus}`}>
                            <Circle size={10} fill="currentColor" />
                            <span>
                              {graphData.calendar.currentStatus === 'free'
                                ? (() => {
                                    if (!graphData.workingHours) return 'Free now';
                                    let tz;
                                    try {
                                      if (graphData.workingHours.timeZone) {
                                        Intl.DateTimeFormat(undefined, { timeZone: graphData.workingHours.timeZone });
                                        tz = graphData.workingHours.timeZone;
                                      }
                                    } catch { /* invalid tz */ }
                                    const now = new Date();
                                    const todayInTz = now.toLocaleDateString('en-US', { weekday: 'long', ...(tz && { timeZone: tz }) }).toLowerCase();
                                    const isWorkingDay = graphData.workingHours.daysOfWeek.map(d => d.toLowerCase()).includes(todayInTz);
                                    if (!isWorkingDay) return 'Free (outside working hours)';
                                    const [sH, sM] = graphData.workingHours.startTime.split(':').map(Number);
                                    const [eH, eM] = graphData.workingHours.endTime.split(':').map(Number);
                                    if (tz) {
                                      const tzNowStr = now.toLocaleString('en-US', { timeZone: tz });
                                      const tzNowAsLocal = new Date(tzNowStr).getTime();
                                      const offsetMs = now.getTime() - tzNowAsLocal;
                                      const dateStr = now.toLocaleDateString('en-CA', { timeZone: tz });
                                      const [y, m, d] = dateStr.split('-').map(Number);
                                      const wStart = new Date(y, m - 1, d, sH, sM).getTime() + offsetMs;
                                      const wEnd = new Date(y, m - 1, d, eH, eM).getTime() + offsetMs;
                                      if (now.getTime() < wStart || now.getTime() >= wEnd) return 'Free (outside working hours)';
                                    } else {
                                      const wStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), sH, sM).getTime();
                                      const wEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), eH, eM).getTime();
                                      if (now.getTime() < wStart || now.getTime() >= wEnd) return 'Free (outside working hours)';
                                    }
                                    return 'Free now';
                                  })()
                                : graphData.calendar.currentStatus === 'oof'
                                  ? graphData.calendar.currentEvent?.isAllDay
                                    ? 'Out of Office'
                                    : `Out of Office until ${new Date(graphData.calendar.currentEvent.end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                                  : `In a meeting${graphData.calendar.currentEvent?.end && !graphData.calendar.currentEvent?.isAllDay ? ` until ${new Date(graphData.calendar.currentEvent.end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}`
                              }
                            </span>
                          </div>
                          {graphData.calendar.currentStatus === 'oof' && graphData.oofMessage?.message && (
                            <div className="oof-message-container">
                              <div className="oof-message-text">
                                {oofExpanded
                                  ? graphData.oofMessage.message
                                  : graphData.oofMessage.message.length > 100
                                    ? (graphData.oofMessage.message.slice(0, 100).replace(/\s+\S*$/, '') || graphData.oofMessage.message.slice(0, 100)) + '...'
                                    : graphData.oofMessage.message
                                }
                              </div>
                              {graphData.oofMessage.message.length > 100 && (
                                <button
                                  className="oof-see-more"
                                  onClick={() => setOofExpanded(prev => !prev)}
                                >
                                  {oofExpanded ? 'See Less' : 'See More'}
                                </button>
                              )}
                            </div>
                          )}
                          {graphData.calendar.currentStatus === 'free' && graphData.calendar.nextBusy && (
                            <div className="calendar-next">
                              <Clock size={12} />
                              <span>Next meeting at {new Date(graphData.calendar.nextBusy.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                            </div>
                          )}
                          {/* Timeline strip */}
                          <div className="timeline-strip">
                            <div className="timeline-hours">
                              {Array.from({ length: 9 }, (_, i) => {
                                const hour = new Date(graphData.calendar.windowStart);
                                hour.setHours(hour.getHours() + i);
                                return (
                                  <span key={i} className="timeline-hour-label">
                                    {hour.toLocaleTimeString([], { hour: 'numeric' })}
                                  </span>
                                );
                              })}
                            </div>
                            <div className="timeline-bar">
                              {/* Non-working hours overlay */}
                              {graphData.workingHours && (() => {
                                const windowStart = new Date(graphData.calendar.windowStart).getTime();
                                const windowEnd = new Date(graphData.calendar.windowEnd).getTime();
                                const windowDuration = windowEnd - windowStart;

                                // Validate IANA timezone; fall back to local if invalid
                                let tz;
                                try {
                                  if (graphData.workingHours.timeZone) {
                                    Intl.DateTimeFormat(undefined, { timeZone: graphData.workingHours.timeZone });
                                    tz = graphData.workingHours.timeZone;
                                  }
                                } catch { /* invalid tz — use local */ }

                                // Check if today is a working day (in the user's tz)
                                const todayInTz = new Date().toLocaleDateString('en-US', { weekday: 'long', ...(tz && { timeZone: tz }) }).toLowerCase();
                                const isWorkingDay = graphData.workingHours.daysOfWeek
                                  .map(d => d.toLowerCase())
                                  .includes(todayInTz);

                                if (!isWorkingDay) {
                                  return <div className="timeline-nonworking" style={{ left: '0%', width: '100%' }} />;
                                }

                                // Parse working hours into epoch ms
                                const [startH, startM] = graphData.workingHours.startTime.split(':').map(Number);
                                const [endH, endM] = graphData.workingHours.endTime.split(':').map(Number);
                                const now = new Date();

                                // Get the user's current date in their tz, then build
                                // work start/end as UTC epoch ms using the tz offset
                                let workStart, workEnd;
                                if (tz) {
                                  // Find UTC offset of the user's tz by comparing epoch of
                                  // "now in tz rendered as local" vs actual now
                                  const tzNowStr = now.toLocaleString('en-US', { timeZone: tz });
                                  const tzNowAsLocal = new Date(tzNowStr).getTime();
                                  const offsetMs = now.getTime() - tzNowAsLocal;

                                  // Get today's date in the user's tz
                                  const dateStr = now.toLocaleDateString('en-CA', { timeZone: tz });
                                  const [y, m, d] = dateStr.split('-').map(Number);

                                  // Build work times as "local" dates, then shift by offset to get UTC epoch
                                  workStart = new Date(y, m - 1, d, startH, startM).getTime() + offsetMs;
                                  workEnd = new Date(y, m - 1, d, endH, endM).getTime() + offsetMs;
                                } else {
                                  workStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), startH, startM).getTime();
                                  workEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), endH, endM).getTime();
                                }

                                const overlays = [];

                                // Before working hours
                                if (windowStart < workStart && workStart < windowEnd) {
                                  const width = ((workStart - windowStart) / windowDuration) * 100;
                                  overlays.push(<div key="pre" className="timeline-nonworking" style={{ left: '0%', width: `${width}%` }} />);
                                  overlays.push(<div key="start-line" className="timeline-work-boundary" style={{ left: `${width}%` }} />);
                                }

                                // After working hours
                                if (workEnd > windowStart && workEnd < windowEnd) {
                                  const left = ((workEnd - windowStart) / windowDuration) * 100;
                                  const width = ((windowEnd - workEnd) / windowDuration) * 100;
                                  overlays.push(<div key="end-line" className="timeline-work-boundary" style={{ left: `${left}%` }} />);
                                  overlays.push(<div key="post" className="timeline-nonworking" style={{ left: `${left}%`, width: `${width}%` }} />);
                                } else if (workEnd <= windowStart) {
                                  overlays.push(<div key="all" className="timeline-nonworking" style={{ left: '0%', width: '100%' }} />);
                                }

                                return overlays;
                              })()}
                              {/* Now indicator */}
                              <div className="timeline-now" style={{ left: '0%' }} />
                              {/* Busy blocks */}
                              {graphData.calendar.events
                                .filter(e => e.showAs === 'busy')
                                .map((event, idx) => {
                                  const windowStart = new Date(graphData.calendar.windowStart).getTime();
                                  const windowEnd = new Date(graphData.calendar.windowEnd).getTime();
                                  const windowDuration = windowEnd - windowStart;
                                  const eventStart = Math.max(new Date(event.start).getTime(), windowStart);
                                  const eventEnd = Math.min(new Date(event.end).getTime(), windowEnd);
                                  const left = ((eventStart - windowStart) / windowDuration) * 100;
                                  const width = ((eventEnd - eventStart) / windowDuration) * 100;
                                  return (
                                    <div
                                      key={`busy-${idx}`}
                                      className="timeline-busy-block"
                                      style={{ left: `${left}%`, width: `${width}%` }}
                                      title="Busy"
                                    />
                                  );
                                })}
                              {/* Out of Office blocks */}
                              {graphData.calendar.events
                                .filter(e => e.showAs === 'oof')
                                .map((event, idx) => {
                                  const windowStart = new Date(graphData.calendar.windowStart).getTime();
                                  const windowEnd = new Date(graphData.calendar.windowEnd).getTime();
                                  const windowDuration = windowEnd - windowStart;
                                  const eventStart = Math.max(new Date(event.start).getTime(), windowStart);
                                  const eventEnd = Math.min(new Date(event.end).getTime(), windowEnd);
                                  const left = ((eventStart - windowStart) / windowDuration) * 100;
                                  const width = ((eventEnd - eventStart) / windowDuration) * 100;
                                  return (
                                    <div
                                      key={`oof-${idx}`}
                                      className="timeline-oof-block"
                                      style={{ left: `${left}%`, width: `${width}%` }}
                                      title="Out of Office"
                                    />
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="section-unavailable">
                          Unable to load
                          <button className="retry-btn" onClick={() => graphFetch('calendar', selectedPerson.email)}>Retry</button>
                        </div>
                      )}
                    </div>

                    {/* Organization Section */}
                    {(graphData.manager?.manager || (graphData.directReports?.directReports?.length > 0)) && (
                      <div className="profile-section">
                        <div className="section-label">
                          <Users size={14} />
                          <span>Organization</span>
                        </div>
                        {graphData.manager?.manager && (
                          <div
                            className="org-person-row clickable"
                            onClick={() => {
                              const mgr = graphData.manager.manager;
                              if (mgr.email) {
                                const mgrPerson = allPeopleAcrossBuildings.find(p => p.email?.toLowerCase() === mgr.email.toLowerCase());
                                if (mgrPerson) handlePersonClick(mgrPerson);
                              }
                            }}
                          >
                            <UserCheck size={14} />
                            <div>
                              <div className="org-person-name">Reports to: {graphData.manager.manager.name}</div>
                              {graphData.manager.manager.jobTitle && (
                                <div className="org-person-title">{graphData.manager.manager.jobTitle}</div>
                              )}
                              <div className="org-person-location">
                                <MapPin size={10} />
                                {(() => {
                                  const csvPerson = allPeopleAcrossBuildings.find(p => p.email?.toLowerCase() === graphData.manager.manager.email?.toLowerCase());
                                  return csvPerson ? `${csvPerson.buildingName} • ${csvPerson.location}` : (graphData.manager.manager.officeLocation || 'No location');
                                })()}
                              </div>
                            </div>
                          </div>
                        )}
                        {graphData.directReports?.directReports?.length > 0 && (
                          <div className="direct-reports-section">
                            <div className="org-person-row">
                              <Users size={14} />
                              <span className="org-person-name">{graphData.directReports.directReports.length} direct report{graphData.directReports.directReports.length !== 1 ? 's' : ''}</span>
                            </div>
                            {graphData.directReports.directReports.map((report, idx) => (
                              <div
                                key={idx}
                                className="org-person-row clickable sub-report"
                                onClick={() => {
                                  if (report.email) {
                                    const reportPerson = allPeopleAcrossBuildings.find(p => p.email?.toLowerCase() === report.email.toLowerCase());
                                    if (reportPerson) handlePersonClick(reportPerson);
                                  }
                                }}
                              >
                                <User size={12} />
                                <div>
                                  <div className="org-person-name">{report.name}</div>
                                  {report.jobTitle && <div className="org-person-title">{report.jobTitle}</div>}
                                  <div className="org-person-location">
                                    <MapPin size={10} />
                                    {(() => {
                                      const csvPerson = allPeopleAcrossBuildings.find(p => p.email?.toLowerCase() === report.email?.toLowerCase());
                                      return csvPerson ? `${csvPerson.buildingName} • ${csvPerson.location}` : (report.officeLocation || 'No location');
                                    })()}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {(graphData.loading.manager || graphData.loading.reports) && (
                      <div className="shimmer-block" />
                    )}

              </div>
            </motion.div>
          ) : selectedRoom ? (
            selectedRoom.displayName ? (
              selectedRoom.isPersonalSpace ? (
                // ── Personal Office Panel (CABIN-1/2, HR ROOM) ─────────────
                <motion.div
                  className="room-details"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="details-header">
                    <h3>Workspace</h3>
                    <button className="close-details-btn" onClick={() => setSelectedRoom(null)}>×</button>
                  </div>

                  <div className="details-info">
                    <div className="info-row">
                      <Building2 size={18} />
                      <span>{allBuildings.find(b => b.id === selectedBuilding)?.name}</span>
                    </div>
                    <div className="info-row">
                      <MapPin size={18} />
                      <span>{selectedRoom.displayName}</span>
                    </div>
                  </div>

                  {selectedRoom.people && selectedRoom.people.length > 0 ? (
                    <div className="room-people-list">
                      <h4>{selectedRoom.people.length} {selectedRoom.people.length === 1 ? 'Person' : 'People'}</h4>
                      {selectedRoom.people.map((person, idx) => {
                        const pData = person.email ? roomPeopleData[person.email.toLowerCase()] : null;
                        const presenceLabel = pData?.presence ? ({
                          Available: 'Available', AvailableIdle: 'Available',
                          Busy: 'Busy', BusyIdle: 'Busy', DoNotDisturb: 'Do Not Disturb',
                          Away: 'Away', BeRightBack: 'Be Right Back', Offline: 'Offline',
                          PresenceUnknown: null,
                        })[pData.presence] || null : null;
                        return (
                          <div
                            key={idx}
                            className="room-person-item personal-office-person"
                            onClick={() => handlePersonClick({
                              ...person,
                              location: selectedRoom.displayName,
                              building: selectedBuilding,
                              buildingName: allBuildings.find(b => b.id === selectedBuilding)?.name
                            })}
                          >
                            <div className="room-person-avatar">
                              {pData?.photo
                                ? <img src={pData.photo} alt={person.name} className="room-person-photo" />
                                : <span className="room-person-initials">
                                    {person.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                  </span>
                              }
                              {pData?.presence && (
                                <span className={`avatar-presence-dot presence-dot-${pData.presence.toLowerCase()}`} />
                              )}
                            </div>
                            <div className="room-person-info">
                              <div className="person-item-name">{person.name}</div>
                              {person.title && <div className="person-item-title">{person.title}</div>}
                              {presenceLabel && (
                                <div className={`room-person-presence presence-text-${pData.presence.toLowerCase()}`}>
                                  <Circle size={8} fill="currentColor" />
                                  <span>{presenceLabel}</span>
                                </div>
                              )}
                              {person.email && (
                                <div className="person-item-email">{person.email}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="cabin-empty-state">
                      <User size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                      <span>No one assigned</span>
                    </div>
                  )}
                </motion.div>
              ) : (
              // Conference Room Details
              <motion.div
                className="room-details"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="details-header">
                  {!/^(CABIN|M\.CABIN)/i.test(selectedRoom.displayName || '') && (
                    <h3>{
                      /^HR ROOM/i.test(selectedRoom.displayName || '') ? 'HR Room' :
                      selectedRoom.isPersonalSpace ? 'Personal Space' :
                      'Conference Room'
                    }</h3>
                  )}
                  <button className="close-details-btn" onClick={() => setSelectedRoom(null)}>×</button>
                </div>
                
                <div className="room-header">
                  <Building2 size={32} className="room-icon" />
                  <h2 className="room-name">{selectedRoom.displayName}</h2>
                </div>
                
                <div className="details-info">
                  <div className="info-row">
                    <Building2 size={18} />
                    <span>{allBuildings.find(b => b.id === selectedBuilding)?.name}</span>
                  </div>
                  {/* Removed redundant location row — the room name already
                      headlines the panel; repeating it here was just noise. */}
                  {selectedRoom.capacity && (
                    <div className="info-row">
                      <Users size={18} />
                      <span>Capacity: {selectedRoom.capacity} persons</span>
                    </div>
                  )}
                </div>

                {/* Conference Room Availability with Timeline.
                    Skipped entirely for personal spaces (CABIN/HR/HUDDLE) — they
                    aren't bookable, so we only show the people inside. */}
                {!selectedRoom.isPersonalSpace && (() => {
                  const calData = roomAvailability[selectedRoom.id];
                  // A room has a calendar if it carries its own mailbox
                  // (HYD.Red@..., etc.) OR an occupant email — either is a
                  // valid Graph calendar target.
                  const hasEmail = selectedRoom.email || selectedRoom.people?.[0]?.email;
                  if (!hasEmail) return (
                    <div className="conference-availability">
                      <div className="section-label"><Calendar size={14} /><span>Availability</span></div>
                      <div className="calendar-status" style={{ color: 'var(--text-secondary)' }}>
                        <Circle size={10} fill="currentColor" />
                        <span>No calendar configured</span>
                      </div>
                    </div>
                  );
                  if (!calData) return <div className="shimmer-block" style={{ height: '40px', margin: '12px 0' }} />;
                  return (
                    <div className="conference-availability">
                      <div className="section-label">
                        <Calendar size={14} />
                        <span>Availability</span>
                      </div>
                      <div className={`calendar-status ${calData.currentStatus === 'free' ? 'free' : 'busy'}`}>
                        <Circle size={10} fill="currentColor" />
                        <span>
                          {calData.currentStatus === 'free'
                            ? 'Available now'
                            : `Occupied${calData.currentEvent?.end && !calData.currentEvent?.isAllDay ? ` until ${new Date(calData.currentEvent.end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}`
                          }
                        </span>
                      </div>
                      {calData.currentStatus === 'free' && calData.nextBusy && (
                        <div className="calendar-next">
                          <Clock size={12} />
                          <span>Next booking at {new Date(calData.nextBusy.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                        </div>
                      )}
                      <div className="timeline-strip">
                        <div className="timeline-hours">
                          {Array.from({ length: 9 }, (_, i) => {
                            const hour = new Date(calData.windowStart);
                            hour.setHours(hour.getHours() + i);
                            return (
                              <span key={i} className="timeline-hour-label">
                                {hour.toLocaleTimeString([], { hour: 'numeric' })}
                              </span>
                            );
                          })}
                        </div>
                        <div className="timeline-bar">
                          <div className="timeline-now" style={{ left: '0%' }} />
                          {calData.events
                            .filter(e => e.showAs === 'busy' || e.showAs === 'oof')
                            .map((event, idx) => {
                              const windowStart = new Date(calData.windowStart).getTime();
                              const windowEnd = new Date(calData.windowEnd).getTime();
                              const windowDuration = windowEnd - windowStart;
                              const eventStart = Math.max(new Date(event.start).getTime(), windowStart);
                              const eventEnd = Math.min(new Date(event.end).getTime(), windowEnd);
                              const left = ((eventStart - windowStart) / windowDuration) * 100;
                              const width = ((eventEnd - eventStart) / windowDuration) * 100;
                              return (
                                <div
                                  key={idx}
                                  className="timeline-busy-block"
                                  style={{ left: `${left}%`, width: `${width}%` }}
                                />
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Book via Outlook — shown for rooms that have a room mailbox email.
                    Excluded for personal spaces (CABIN-1/2, HR ROOM) whose
                    people[] holds employee emails, not room mailboxes. */}
                {(() => {
                  // Prefer the room's own mailbox (HYD.Red@..., etc.) over any
                  // CSV-assigned occupant email, so the booking always goes to
                  // the room calendar rather than a person.
                  const roomEmail = selectedRoom.email || selectedRoom.people?.[0]?.email;
                  if (roomEmail && !selectedRoom.isPersonalSpace) {
                    return (
                      <button
                        className="book-room-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // Outlook web compose only honors documented params:
                          // subject / body / to / cc / bcc / location / startdt
                          // / enddt / allday. `rooms=` is silently dropped — the
                          // working pattern is `to=<room mailbox>` (room becomes
                          // a required attendee → auto-accepts when free) plus
                          // `location=<displayName>` for the human-readable text.
                          const outlookUrl = `https://outlook.office.com/calendar/action/compose?subject=${encodeURIComponent(`Meeting in ${selectedRoom.displayName}`)}&location=${encodeURIComponent(selectedRoom.displayName)}&to=${encodeURIComponent(roomEmail)}`;
                          window.open(outlookUrl, '_blank');
                        }}
                      >
                        <Calendar size={18} />
                        <span>Book Room</span>
                      </button>
                    );
                  }
                  return null;
                })()}

                {/* People assigned to this room (e.g. CABIN-1, HR ROOM) */}
                {selectedRoom.people && selectedRoom.people.length > 0 && (
                  <div className="room-people-list">
                    <h4>{selectedRoom.people.length} {selectedRoom.people.length === 1 ? 'Person' : 'People'}</h4>
                    {selectedRoom.people.map((person, idx) => {
                      const pData = person.email ? roomPeopleData[person.email.toLowerCase()] : null;
                      const presenceLabel = pData?.presence ? {
                        Available: 'Available', AvailableIdle: 'Available - Idle',
                        Busy: 'Busy', BusyIdle: 'Busy - Idle', DoNotDisturb: 'Do Not Disturb',
                        Away: 'Away', BeRightBack: 'Be Right Back', Offline: 'Offline',
                        PresenceUnknown: null,
                      }[pData.presence] || null : null;
                      return (
                        <div
                          key={idx}
                          className="room-person-item"
                          onClick={() => handlePersonClick({
                            ...person,
                            location: selectedRoom.displayName,
                            locationId: selectedRoom.id,
                            building: selectedBuilding,
                            buildingName: allBuildings.find(b => b.id === selectedBuilding)?.name
                          })}
                        >
                          <div className="room-person-avatar">
                            {pData?.photo ? (
                              <img src={pData.photo} alt={person.name} className="room-person-photo" />
                            ) : (
                              <span className="room-person-initials">
                                {person.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="room-person-info">
                            <div className="person-item-name">{person.name}</div>
                            {presenceLabel && (
                              <div className={`room-person-presence presence-text-${pData.presence.toLowerCase()}`}>
                                <Circle size={8} fill="currentColor" />
                                <span>{presenceLabel}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
              ) // end !isPersonalSpace conference room
            ) : (
              // Regular Office Room Details (with people)
              <motion.div
                className="room-details"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="details-header">
                  <h3>Room Details</h3>
                  <button className="close-details-btn" onClick={() => setSelectedRoom(null)}>×</button>
                </div>
                
                <div className="room-header">
                  <Building2 size={32} className="room-icon" />
                  <h2 className="room-name">{selectedRoom.id}</h2>
                </div>
                
                <div className="room-people-list">
                  <h4>{selectedRoom.people.length} {selectedRoom.people.length === 1 ? 'Person' : 'People'}</h4>
                  {selectedRoom.people.map((person, idx) => {
                    const pData = person.email ? roomPeopleData[person.email.toLowerCase()] : null;
                    const presenceLabel = pData?.presence ? {
                      Available: 'Available', AvailableIdle: 'Available - Idle',
                      Busy: 'Busy', BusyIdle: 'Busy - Idle', DoNotDisturb: 'Do Not Disturb',
                      Away: 'Away', BeRightBack: 'Be Right Back', Offline: 'Offline',
                      PresenceUnknown: null,
                    }[pData.presence] || null : null;
                    return (
                      <div
                        key={idx}
                        className="room-person-item"
                        onClick={() => handlePersonClick({
                          ...person,
                          location: selectedRoom.id,
                          building: selectedBuilding,
                          buildingName: allBuildings.find(b => b.id === selectedBuilding)?.name
                        })}
                      >
                        <div className="room-person-avatar">
                          {pData?.photo ? (
                            <img src={pData.photo} alt={person.name} className="room-person-photo" />
                          ) : (
                            <span className="room-person-initials">
                              {person.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="room-person-info">
                          <div className="person-item-name">{person.name}</div>
                          {presenceLabel && (
                            <div className={`room-person-presence presence-text-${pData.presence.toLowerCase()}`}>
                              <Circle size={8} fill="currentColor" />
                              <span>{presenceLabel}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )
          ) : (
            showEmptyDesks && buildingData?.cubes?.some(c => !c.people || c.people.length === 0) ? (
              <motion.div
                className="empty-desks-panel"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="details-header">
                  <h3>Empty Desks</h3>
                  <button className="close-details-btn" onClick={() => setShowEmptyDesks(false)}>×</button>
                </div>
                <div className="results-header-panel">
                  <MapPin size={18} />
                  <span>
                    {buildingData.cubes.filter(c => !c.people || c.people.length === 0).length} Empty {buildingData.cubes.filter(c => !c.people || c.people.length === 0).length === 1 ? 'Desk' : 'Desks'}
                  </span>
                </div>
                <div className="empty-desks-list">
                  {buildingData.cubes
                    .filter(c => !c.people || c.people.length === 0)
                    .slice()
                    .sort((a, b) => {
                      const la = a.displayLabel || a.id;
                      const lb = b.displayLabel || b.id;
                      const na = parseFloat(la);
                      const nb = parseFloat(lb);
                      if (!isNaN(na) && !isNaN(nb)) return na - nb;
                      return la.localeCompare(lb);
                    })
                    .map((cube) => {
                      const label = cube.displayLabel || cube.id;
                      const isActive = activeDeskId === cube.id;
                      return (
                        <div
                          key={cube.id}
                          className={`empty-desk-item${isActive ? ' empty-desk-item--active' : ''}`}
                          onClick={() => {
                            setActiveDeskId(prev => prev === cube.id ? null : cube.id);
                            const target = document.querySelector(`[data-location-id="${CSS.escape(cube.id)}"]`);
                            if (target) {
                              target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                            }
                          }}
                        >
                          <span className="empty-desk-id">{label}</span>
                          {isSeatingAdmin && (
                            <button
                              type="button"
                              className="empty-desk-assign-btn"
                              title={`Assign someone to ${cube.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                openAddSeatPanel(cube.id);
                              }}
                            >
                              <UserPlus size={11} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                </div>
              </motion.div>
            ) : (
            <div className="no-selection">
              <User size={64} strokeWidth={1} />
              <p>Select a person or room to view details</p>
            </div>
            )
          )}
        </div>
      </div>

      {/* Hover Tooltip */}
      {hoveredPerson && (
        <div
          className="seat-tooltip"
          style={{
            position: 'fixed',
            left: hoveredPerson.x + 18,
            top: hoveredPerson.y - 12,
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        >
          <div className="seat-tooltip-name">{hoveredPerson.name}</div>
          {hoveredPerson.title && <div className="seat-tooltip-title">{hoveredPerson.title}</div>}
          {hoveredPerson.email && <div className="seat-tooltip-email">{hoveredPerson.email}</div>}
          <div className="seat-tooltip-desk">
            <MapPin size={10} />
            <span>{hoveredPerson.deskId}</span>
          </div>
          {hoveredPerson.presence && (
            <div className={`seat-tooltip-presence presence-${hoveredPerson.presence.toLowerCase()}`}>
              <Circle size={8} fill="currentColor" />
              <span>{hoveredPerson.presence.replace(/([A-Z])/g, ' $1').trim()}</span>
            </div>
          )}
        </div>
      )}

      {/* ── Employee Seat Change Request ───────────────────────────────── */}
      {showSeatRequestPanel && currentSeatingOffice && (
        <div className="manage-overlay" onClick={() => setShowSeatRequestPanel(false)}>
          <div
            className="manage-panel seat-request-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="seat-request-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="manage-panel-header">
              <div className="manage-panel-title">
                <h3 id="seat-request-title">Request Seat Change</h3>
                <span className="history-panel-subtitle">{currentCampus?.name || selectedCampus}</span>
              </div>
              <button
                className="manage-close-btn"
                onClick={() => setShowSeatRequestPanel(false)}
                aria-label="Close seat request"
              >
                <XIcon size={18} />
              </button>
            </div>

            <form
              className="manage-form seat-request-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (!seatRequestLoading) handleSubmitSeatRequest();
              }}
            >
              <div className="seat-request-user">
                <span className="seating-user-avatar">
                  {(user?.name || user?.email || 'U')
                    .split(/\s+/)
                    .map(part => part[0])
                    .filter(Boolean)
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
                <span className="seating-user-copy">
                  <strong>{user?.name || user?.email || 'Unknown user'}</strong>
                  {user?.email && <em>{user.email}</em>}
                </span>
              </div>

              <label>Current Seat</label>
              <input
                className="manage-input"
                placeholder={currentSeatingOffice === 'hyd' ? 'e.g. WS010' : 'Current location'}
                value={seatRequestForm.currentLocation}
                onChange={e => setSeatRequestForm(form => ({ ...form, currentLocation: e.target.value }))}
              />

              <label>{currentSeatingOffice === 'hyd' ? 'New WS ID' : 'New Location'}</label>
              <input
                className="manage-input"
                placeholder={currentSeatingOffice === 'hyd' ? 'e.g. WS020' : 'New location'}
                value={seatRequestForm.newLocation}
                onChange={e => setSeatRequestForm(form => ({ ...form, newLocation: e.target.value }))}
                autoFocus
              />

              <label>Note</label>
              <textarea
                className="manage-input seat-request-note"
                placeholder="Optional"
                value={seatRequestForm.note}
                onChange={e => setSeatRequestForm(form => ({ ...form, note: e.target.value.slice(0, 300) }))}
                rows={3}
              />

              <button type="submit" className="manage-submit-btn" disabled={seatRequestLoading}>
                {seatRequestLoading ? 'Submitting…' : 'Submit Request'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Admin-only Seating History ─────────────────────────────────── */}
      {showHistoryPanel && isSeatingAdmin && currentSeatingOffice && (
        <div className="manage-overlay" onClick={() => setShowHistoryPanel(false)}>
          <div
            className="manage-panel history-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="seat-history-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="manage-panel-header">
              <div className="manage-panel-title">
                <h3 id="seat-history-title">Seating History - {currentCampus?.name}</h3>
                <span className="history-panel-subtitle">10 most recent additions and removals</span>
              </div>
              <button
                className="manage-close-btn"
                onClick={() => setShowHistoryPanel(false)}
                aria-label="Close seating history"
              >
                <XIcon size={18} />
              </button>
            </div>

            <div className="history-list">
              {historyLoading && (
                <div className="history-state">Loading history…</div>
              )}
              {!historyLoading && historyError && (
                <div className="history-state history-state-error">{historyError}</div>
              )}
              {!historyLoading && !historyError && seatHistory.length === 0 && (
                <div className="history-empty">
                  <HistoryIcon size={24} />
                  <span>No seating changes recorded yet.</span>
                </div>
              )}
              {!historyLoading && seatHistory.map(entry => {
                const buildingLabel = entry.building
                  ? (allBuildings.find(b => b.id === entry.building || b.drawioName === entry.building)?.name || entry.building)
                  : '';
                const isAdded = entry.action === 'added';
                return (
                  <div className="history-entry" key={entry.id || `${entry.timestamp}-${entry.email}-${entry.location}`}>
                    <div className={`history-action-icon ${isAdded ? 'added' : 'removed'}`}>
                      {isAdded ? <UserPlus size={15} /> : <Trash2 size={15} />}
                    </div>
                    <div className="history-entry-content">
                      <div className="history-entry-title">
                        <span className={isAdded ? 'history-added' : 'history-removed'}>
                          {isAdded ? 'Added' : 'Removed'}
                        </span>
                        <strong>{entry.name || entry.email || 'Unknown user'}</strong>
                      </div>
                      {entry.email && entry.email !== entry.name && (
                        <div className="history-entry-email">{entry.email}</div>
                      )}
                      <div className="history-entry-location">
                        <MapPin size={11} />
                        <span>{[buildingLabel, entry.location].filter(Boolean).join(' · ') || 'Location unavailable'}</span>
                      </div>
                      <div className="history-entry-meta">
                        <span>{formatSeatHistoryDate(entry.timestamp)}</span>
                        {entry.performedBy && <span>by {entry.performedBy}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      )}

      {/* ── Seating Options ─────────────────────────────────────────────── */}
      {showOptionsPanel && (
        <div className="manage-overlay" onClick={() => setShowOptionsPanel(false)}>
          <div
            className="manage-panel seating-options-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="seating-options-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="manage-panel-header">
              <div className="manage-panel-title">
                <h3 id="seating-options-title">Seating Chart - {currentCampus?.name}</h3>
                <span className="history-panel-subtitle">
                  {seatingOptionsView === 'requests'
                    ? `${seatRequests.length} pending ${seatRequests.length === 1 ? 'request' : 'requests'}`
                    : `${assignedPeopleForCurrentCampus.length} assigned ${assignedPeopleForCurrentCampus.length === 1 ? 'employee' : 'employees'}`}
                </span>
              </div>
              <button
                className="manage-close-btn"
                onClick={() => setShowOptionsPanel(false)}
                aria-label="Close seating options"
              >
                <XIcon size={18} />
              </button>
            </div>

            <div className="seating-options-view-tabs">
              <button
                type="button"
                className={seatingOptionsView === 'employees' ? 'active' : ''}
                onClick={() => setSeatingOptionsView('employees')}
              >
                List of employees
              </button>
              <button
                type="button"
                className={seatingOptionsView === 'requests' ? 'active' : ''}
                onClick={() => setSeatingOptionsView('requests')}
              >
                Seat change requests
              </button>
              <button
                type="button"
                className={seatingOptionsView === 'joiners' ? 'active' : ''}
                onClick={() => setSeatingOptionsView('joiners')}
              >
                Joiners &amp; Exits
              </button>
            </div>

            {seatingOptionsView === 'employees' && (
              <>
                <div className="seating-options-summary">
                  <div>
                    <span>Total Employees</span>
                    <strong>{assignedPeopleForCurrentCampus.length}</strong>
                  </div>
                  <div>
                    <span>Office</span>
                    <strong>{currentCampus?.name || selectedCampus}</strong>
                  </div>
                </div>

                <div className="seating-options-toolbar">
                  <div className="seating-options-section-title">
                    <ListFilter size={14} />
                    <span>List of employees</span>
                  </div>
                  <label className="seating-sort-control">
                    <ArrowUpDown size={14} />
                    <span>Sort</span>
                    <select
                      value={employeeListSort}
                      onChange={e => setEmployeeListSort(e.target.value)}
                      aria-label="Sort employee list"
                    >
                      <option value="ws-asc">WS ID ascending</option>
                      <option value="ws-desc">WS ID descending</option>
                      <option value="name-asc">Name A-Z</option>
                      <option value="name-desc">Name Z-A</option>
                      <option value="email-asc">Email A-Z</option>
                      <option value="building-seat">Office / seat</option>
                    </select>
                  </label>
                </div>

                <div className="seating-all-users-list">
                  {assignedPeopleForCurrentCampus.length === 0 ? (
                    <div className="history-empty">
                      <Users size={24} />
                      <span>No assigned users found.</span>
                    </div>
                  ) : assignedPeopleForCurrentCampus.map(person => (
                    <button
                      type="button"
                      className="seating-user-row"
                      key={`${person.building}-${person.locationId || person.location}-${person.email || person.name}`}
                      onClick={() => {
                        setShowOptionsPanel(false);
                        handlePersonClick(person);
                      }}
                    >
                      <span className="seating-user-avatar">
                        {(person.name || person.email || 'U')
                          .split(/\s+/)
                          .map(part => part[0])
                          .filter(Boolean)
                          .join('')
                          .slice(0, 2)
                          .toUpperCase()}
                      </span>
                      <span className="seating-user-copy">
                        <strong>{person.name || person.email || 'Unknown user'}</strong>
                        {person.email && <em>{person.email}</em>}
                      </span>
                      <span className="seating-user-location">
                        <MapPin size={12} />
                        {[person.buildingName, person.location].filter(Boolean).join(' · ')}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {seatingOptionsView === 'joiners' && (
              <div className="seat-requests-view">
                <div className="seating-options-toolbar">
                  <div className="seating-options-section-title">
                    <HistoryIcon size={14} />
                    <span>Recent joiners &amp; exits (last 30 days)</span>
                  </div>
                  <button
                    type="button"
                    className="seat-requests-refresh"
                    onClick={loadJoinersExitsLog}
                    disabled={joinersExitsLoading}
                  >
                    Refresh
                  </button>
                </div>
                <div className="seat-requests-list">
                  {joinersExitsLoading && <div className="history-state">Loading…</div>}
                  {!joinersExitsLoading && joinersExitsError && (
                    <div className="history-state history-state-error">{joinersExitsError}</div>
                  )}
                  {!joinersExitsLoading && !joinersExitsError && joinersExitsLog.length === 0 && (
                    <div className="history-empty">
                      <HistoryIcon size={24} />
                      <span>No joiner or exit events recorded yet.</span>
                    </div>
                  )}
                  {!joinersExitsLoading && !joinersExitsError && joinersExitsLog.map(entry => {
                    const assignedSeat = entry.email
                      ? allPeopleAcrossBuildings.find(p => normalizeEmployeeEmail(p.email) === normalizeEmployeeEmail(entry.email))
                      : null;
                    const wsId = assignedSeat?.locationId || assignedSeat?.location || null;
                    return (
                    <div className="history-entry" key={entry.id}>
                      <div className={`history-action-icon ${entry.action}`}>
                        {entry.action === 'added' ? <UserPlus size={15} /> : <Trash2 size={15} />}
                      </div>
                      <div className="history-entry-content">
                        <div className="history-entry-title">
                          <span className={entry.action === 'added' ? 'history-added' : 'history-removed'}>
                            {entry.action === 'added' ? 'Joined' : entry.action === 'disabled' ? 'Disabled' : 'Removed'}
                          </span>
                          <strong>{entry.name}</strong>
                          {wsId && (
                            <span className="joiner-ws-badge">
                              <MapPin size={10} />
                              {wsId}
                            </span>
                          )}
                        </div>
                        {entry.email && <div className="history-entry-email">{entry.email}</div>}
                        {(entry.jobTitle || entry.department || entry.officeLocation) && (
                          <div className="history-entry-meta">
                            {[entry.jobTitle, entry.department, entry.officeLocation].filter(Boolean).join(' · ')}
                          </div>
                        )}
                        <div className="history-entry-meta">{formatSeatHistoryDate(entry.timestamp)}</div>
                      </div>
                      {entry.action === 'added' && !wsId && (
                        <button
                          type="button"
                          className="manage-seats-btn"
                          style={{ flexShrink: 0, fontSize: '11px', padding: '4px 10px' }}
                          title="Assign a seat"
                          onClick={() => {
                            setShowOptionsPanel(false);
                            setManageForm({ wsNo: '', name: entry.name || '', email: entry.email || '' });
                            setManageGraphResults([]);
                            setConfirmRemove(null);
                            setManageSearch('');
                            setManageTab('add');
                            setShowManagePanel(true);
                          }}
                        >
                          <UserPlus size={12} />
                          <span>Assign seat</span>
                        </button>
                      )}
                      {entry.action === 'added' && wsId && (
                        <button
                          type="button"
                          className="seating-user-row"
                          style={{ flexShrink: 0, fontSize: '11px', padding: '4px 10px', width: 'auto', display: 'inline-flex', gap: '5px', alignItems: 'center', borderRadius: '8px' }}
                          title="View on floor plan"
                          onClick={() => {
                            setShowOptionsPanel(false);
                            handlePersonClick(assignedSeat);
                          }}
                        >
                          <MapPin size={12} />
                          <span>View seat</span>
                        </button>
                      )}
                      {(entry.action === 'disabled' || entry.action === 'removed') && wsId && (
                        <button
                          type="button"
                          className="manage-remove-btn"
                          style={{ flexShrink: 0, padding: '5px 10px', borderRadius: '7px', display: 'inline-flex', gap: '5px', alignItems: 'center', fontSize: '11px', fontWeight: 700 }}
                          title={`Remove ${entry.name || entry.email} from ${wsId}`}
                          disabled={manageLoading}
                          onClick={async () => {
                            const ok = await handleRemoveSeat(wsId, entry.name, entry.email);
                            if (ok) loadJoinersExitsLog();
                          }}
                        >
                          <Trash2 size={12} />
                          <span>Remove seat</span>
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {seatingOptionsView === 'requests' && (
              <div className="seat-requests-view">
                <div className="seating-options-toolbar">
                  <div className="seating-options-section-title">
                    <Clock size={14} />
                    <span>Pending seat change requests</span>
                  </div>
                  <button
                    type="button"
                    className="seat-requests-refresh"
                    onClick={loadSeatRequests}
                    disabled={seatRequestsLoading}
                  >
                    Refresh
                  </button>
                </div>

                <div className="seat-requests-list">
                  {seatRequestsLoading && <div className="history-state">Loading requests…</div>}
                  {!seatRequestsLoading && seatRequestsError && (
                    <div className="history-state history-state-error">{seatRequestsError}</div>
                  )}
                  {!seatRequestsLoading && !seatRequestsError && seatRequests.length === 0 && (
                    <div className="history-empty">
                      <Clock size={24} />
                      <span>No pending seat change requests.</span>
                    </div>
                  )}
                  {!seatRequestsLoading && !seatRequestsError && seatRequests.map(request => (
                    <div className="seat-request-row" key={request.id}>
                      <div className="seat-request-main">
                        <div className="seat-request-title">
                          <strong>{request.name || request.email || 'Unknown user'}</strong>
                          {request.email && <span>{request.email}</span>}
                        </div>
                        <div className="seat-request-route">
                          <MapPin size={12} />
                          <span>{request.currentLocation || 'Current seat unknown'}</span>
                          <ArrowRight size={12} className="seat-request-route-arrow" />
                          <span>{request.newLocation}</span>
                        </div>
                        <div className="seat-request-meta">
                          {[request.building, formatSeatHistoryDate(request.requestedAt)].filter(Boolean).join(' · ')}
                        </div>
                        {request.note && <div className="seat-request-note-text">{request.note}</div>}
                      </div>
                      <div className="seat-request-actions">
                        <button
                          type="button"
                          className="seat-request-approve"
                          onClick={() => handleReviewSeatRequest(request, 'approve')}
                          disabled={Boolean(seatRequestActionId)}
                        >
                          {seatRequestActionId === `approve-${request.id}` ? 'Approving…' : 'Approve'}
                        </button>
                        <button
                          type="button"
                          className="seat-request-reject"
                          onClick={() => handleReviewSeatRequest(request, 'reject')}
                          disabled={Boolean(seatRequestActionId)}
                        >
                          {seatRequestActionId === `reject-${request.id}` ? 'Rejecting…' : 'Reject'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Manage Seats Panel ─────────────────────────────────────────── */}
      {showManagePanel && (() => {
        const managedSeats = [
          ...(buildingData?.rooms || []),
          ...(buildingData?.cubes || []),
          ...((buildingData?.conferenceRooms || []).filter(room => room.isPersonalSpace))
        ];
        const occupied = managedSeats.filter(c => c.people && c.people.length > 0).length;
        const total = managedSeats.length;
        const isHyd = currentSeatingOffice === 'hyd';
        const officeLabel = selectedBuildingInfo?.campusName || selectedCampus;
        const typedSeat = normalizeSeatId(manageForm.wsNo);
        const isHydWorkstationSeat = isHyd && /^WS\d+$/i.test(typedSeat);
        const seatFieldLabel = isHyd && !typedSeat
          ? 'Workstation ID'
          : (isHydWorkstationSeat ? 'Workstation ID' : 'Location ID');
        const seatPlaceholder = isHyd && !typedSeat
          ? 'e.g. WS059'
          : (isHydWorkstationSeat ? 'e.g. WS059' : (isHyd ? 'e.g. HR ROOM' : 'e.g. 3.C.04'));

        // Live preview: HYD workstations are treated as single-user seats.
        // Shared locations (Pasadena rooms/cubes and HYD rooms like HR ROOM /
        // CABIN-1) support multiple people, so adding a different user should
        // be presented as an additional assignment instead of an overwrite.
        const currentSeat = typedSeat
          ? managedSeats.find(c => c.id.toUpperCase() === typedSeat.toUpperCase()) || null
          : null;
        const currentSeatPeople = currentSeat?.people || [];
        const currentOccupant = currentSeatPeople[0] || null;
        const currentSeatPeopleNames = currentSeatPeople.map(p => p.name).filter(Boolean).join(', ');

        return (
        <div className="manage-overlay" onClick={() => setShowManagePanel(false)}>
          <div className="manage-panel" onClick={e => e.stopPropagation()}>
            <div className="manage-panel-header">
              <div className="manage-panel-title">
                <h3>Manage Seating Chart - {officeLabel}</h3>
                {total > 0 && (
                  <span className="manage-seat-count" title="Occupied / total seats">
                    {occupied} / {total} assigned
                  </span>
                )}
              </div>
              <button className="manage-close-btn" onClick={() => setShowManagePanel(false)}>
                <XIcon size={18} />
              </button>
            </div>

            <div className="manage-tabs">
              <button className={`manage-tab ${manageTab === 'add' ? 'active' : ''}`} onClick={() => setManageTab('add')}>
                <UserPlus size={14} /> Add / Update
              </button>
              <button className={`manage-tab ${manageTab === 'remove' ? 'active' : ''}`} onClick={() => setManageTab('remove')}>
                <Trash2 size={14} /> Remove
              </button>
            </div>

            {manageTab === 'add' && (
              <form
                className="manage-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!manageLoading) handleAddSeat();
                }}
              >
                <label>{seatFieldLabel}</label>
                <input
                  className="manage-input"
                  placeholder={seatPlaceholder}
                  value={manageForm.wsNo}
                  onChange={e => setManageForm(f => ({ ...f, wsNo: e.target.value }))}
                />
                {currentOccupant && (
                  <div className="manage-current-occupant">
                    <UserCheck size={12} />
                    {isHydWorkstationSeat ? (
                      <span>
                        <strong>{typedSeat}</strong> is currently assigned to <strong>{currentOccupant.name}</strong> - saving will overwrite.
                      </span>
                    ) : (
                      <span>
                        <strong>{typedSeat}</strong> currently has <strong>{currentSeatPeopleNames}</strong> - saving will add another person.
                      </span>
                    )}
                  </div>
                )}
                <label>Full Name</label>
                <div className="manage-graph-wrapper">
                  <input
                    className="manage-input"
                    placeholder="e.g. Jane Doe"
                    value={manageForm.name}
                    onChange={e => setManageForm(f => ({ ...f, name: e.target.value, email: '' }))}
                    onKeyDown={e => {
                      // When the directory dropdown is showing, Enter picks the
                      // top match instead of submitting an incomplete form.
                      if (e.key === 'Enter' && manageGraphResults.length > 0) {
                        e.preventDefault();
                        const u = manageGraphResults[0];
                        setManageForm(f => ({ ...f, name: u.name || f.name, email: u.email || f.email }));
                        setManageGraphResults([]);
                      }
                    }}
                    autoComplete="off"
                  />
                  {manageGraphResults.length > 0 && (
                    <div className="manage-graph-dropdown">
                      <div className="manage-graph-hint">
                        <Search size={11} /> Directory matches — click to fill
                      </div>
                      {manageGraphResults.map(u => (
                        <button
                          key={u.email || u.name}
                          type="button"
                          className="manage-graph-result"
                          onClick={() => {
                            setManageForm(f => ({ ...f, name: u.name || f.name, email: u.email || f.email }));
                            setManageGraphResults([]);
                          }}
                        >
                          <div className="manage-graph-name">{u.name}</div>
                          <div className="manage-graph-meta">
                            <span className="manage-graph-email">{u.email}</span>
                            {u.jobTitle && <span className="manage-graph-title"> · {u.jobTitle}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <label>Email</label>
                <input
                  className="manage-input"
                  placeholder="e.g. jane.doe@pinnaclereliability.com"
                  value={manageForm.email}
                  onChange={e => setManageForm(f => ({ ...f, email: e.target.value }))}
                  autoComplete="off"
                />
                <button type="submit" className="manage-submit-btn" disabled={manageLoading}>
                  {manageLoading ? 'Saving…' : isHydWorkstationSeat ? (currentOccupant ? 'Update Assignment' : 'Save Assignment') : 'Add Person'}
                </button>
              </form>
            )}

            {manageTab === 'remove' && (
              <div className="manage-remove">
                <input
                  className="manage-input"
                  placeholder={isHyd ? 'Search by name or workstation…' : 'Search by name or location…'}
                  value={manageSearch}
                  onChange={e => setManageSearch(e.target.value)}
                />
                <div className="manage-people-list">
                  {!manageSearch.trim() ? (
                    <div style={{ padding: '16px', color: 'var(--text-secondary, #888)', textAlign: 'center', fontSize: '13px' }}>
                      {isHyd ? 'Type a name or workstation to search…' : 'Type a name or location to search…'}
                    </div>
                  ) : null}
                  {manageSearch.trim() && allPeopleAcrossBuildings
                    .filter(p => p.building === selectedBuilding)
                    .filter(p => {
                      const q = manageSearch.toLowerCase();
                      return p.name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q) || p.location?.toLowerCase().includes(q);
                    })
                    .slice(0, 50)
                    .map(p => (
                      <div key={`${p.building}-${p.locationId || p.location}-${p.email || p.name}`} className="manage-person-row">
                        <div className="manage-person-info">
                          <span className="manage-person-name">{p.name}</span>
                          <span className="manage-person-ws">{p.location}</span>
                        </div>
                        {confirmRemove?.locationId === (p.locationId || p.location) && confirmRemove?.email === p.email ? (
                          <div className="manage-confirm-row">
                            <span className="manage-confirm-text">Remove?</span>
                            <button
                              className="manage-confirm-yes"
                              onClick={() => { setConfirmRemove(null); handleRemoveSeat(p.locationId || p.location, p.name, p.email); }}
                              disabled={manageLoading}
                            >Yes</button>
                            <button
                              className="manage-confirm-no"
                              onClick={() => setConfirmRemove(null)}
                            >No</button>
                          </div>
                        ) : (
                          <button
                            className="manage-remove-btn"
                            onClick={() => setConfirmRemove({ locationId: p.locationId || p.location, email: p.email, name: p.name })}
                            disabled={manageLoading}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default SeatingChart;
